import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Plus, UserPlus, MessageCircle, StickyNote, CalendarClock, CalendarPlus, ExternalLink,
  Send, ArrowUp, ArrowDown, Trash2, ChevronDown, ChevronUp, RefreshCw, Play, Pause, X, Pencil, Search,
  Mic, StopCircle, Upload, Download, GripVertical, Check, AlertCircle, CheckCircle2,
  Image, Video, Music, FileText, Type, PartyPopper, FileSpreadsheet,
} from 'lucide-react';
import api from '../../services/api';
import Modal from '../../components/shared/Modal';
import ReagendarVisitaModal from '../../components/shared/ReagendarVisitaModal';
import ConfirmarPresencaEventoModal from '../../components/shared/ConfirmarPresencaEventoModal';

// As etapas do funil (id/label/cor/ordem) agora vêm da tabela funil_corretores_estagios via
// GET /parceiros/funil-corretores (campo `estagios`) — configuráveis pelo próprio Kanban
// (criar/renomear/reordenar/excluir), ver ColunaHeader e o rodapé de FunilCorretoresKanban.

const ORIGEM_LABELS = {
  indicacao: 'Indicação',
  prospeccao_ativa: 'Prospecção ativa',
  evento: 'Evento',
  inbound_site: 'Inbound (site)',
  redes_sociais: 'Redes sociais',
  outro: 'Outro',
};

// Endpoints diferem conforme o card ser um lead pré-cadastro ou um corretor já cadastrado.
function interacoesUrl(card) {
  return card.tipo === 'lead' ? `/parceiros/leads/${card.id}/interacoes` : `/parceiros/${card.id}/interacoes`;
}
function estagioUrl(card) {
  return card.tipo === 'lead' ? `/parceiros/leads/${card.id}/estagio` : `/parceiros/${card.id}/estagio`;
}
function visitaUrl(card) {
  return card.tipo === 'lead' ? `/parceiros/leads/${card.id}/visita` : `/parceiros/${card.id}/visita`;
}

// Máscara "44 99948-9948" (DDD + 5+4 dígitos, padrão celular/whatsapp brasileiro). Detecta código
// de país (+55) pela quantidade de dígitos e mostra "+55 44 99948-9948", mas aceita digitar sem
// ele também — sempre grava só os dígitos formatados, sem exigir nenhum dos dois formatos.
function formatWhatsapp(value) {
  let digits = value.replace(/\D/g, '');

  let prefixo = '';
  if (digits.length > 11 && digits.startsWith('55')) {
    prefixo = '+55 ';
    digits = digits.slice(2);
  }
  digits = digits.slice(0, 11);

  const ddd = digits.slice(0, 2);
  const numero = digits.slice(2);

  let formatado = ddd;
  if (numero) {
    const part1 = numero.slice(0, 5);
    const part2 = numero.slice(5, 9);
    formatado += ` ${part1}`;
    if (part2) formatado += `-${part2}`;
  }

  return prefixo + formatado;
}

// Lista manual de destinatários do disparo em massa: uma linha por contato, cada linha no
// formato "Nome, telefone" (vírgula/ponto-e-vírgula/tab separando) ou só o telefone. Retorna
// { nome, telefone (só dígitos) }, deduplicando e ignorando linhas sem número plausível.
function parseListaManual(texto) {
  const out = [];
  const vistos = new Set();
  for (const linha of String(texto || '').split(/\r?\n/)) {
    const t = linha.trim();
    if (!t) continue;
    let nome = '';
    let resto = t;
    const m = t.match(/^(.*?)[,;\t]\s*(.+)$/);
    if (m && /\d/.test(m[2])) { nome = m[1].trim(); resto = m[2]; }
    const tel = resto.replace(/\D/g, '');
    if (tel.length < 10) continue;
    if (vistos.has(tel)) continue;
    vistos.add(tel);
    out.push({ nome, telefone: tel });
  }
  return out;
}

// `evento_confirmacoes.imobiliaria_nome` é texto livre e vem escrito de mil jeitos
// ("Ávura", "Avura Imobiliária", "ÁVURA IMOBILIÁRIA"…). Normaliza pra agrupar: sem acento,
// minúsculo, sem os termos genéricos ("imobiliária", "imóveis", "negócios imobiliários"…).
function normalizaImob(s) {
  return String(s || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\b(imobiliarias?|imoveis|imovel|negocios imobiliarios|solucoes imobiliarias|inteligencia imobiliaria|inteligencia|solucoes|negocios|corretora|consultoria)\b/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function NovoLeadModal({ isOpen, onClose }) {
  const qc = useQueryClient();
  const [nome, setNome] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [origem, setOrigem] = useState('');
  const [tipo, setTipo] = useState('autonomo');
  const [imobiliariaId, setImobiliariaId] = useState('');

  const { data: imobiliarias } = useQuery({
    queryKey: ['imobiliarias'],
    queryFn: () => api.get('/imobiliarias').then(r => r.data.data),
  });

  const criarMut = useMutation({
    mutationFn: () => api.post('/parceiros/leads', {
      nome, whatsapp, origem: origem || null, tipo,
      imobiliaria_id: tipo === 'imobiliaria' ? (imobiliariaId || null) : null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['parceiros-funil-corretores'] });
      onClose();
      setNome(''); setWhatsapp(''); setOrigem(''); setTipo('autonomo'); setImobiliariaId('');
    },
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Novo lead de corretor" size="sm">
      <form onSubmit={e => { e.preventDefault(); criarMut.mutate(); }} className="space-y-3">
        <p className="text-xs text-gray-500">
          Ele ainda não é um corretor cadastrado no sistema — só um contato que você está prospectando.
          Quando ele se cadastrar de verdade (mesmo WhatsApp), o funil migra automaticamente pro cadastro dele.
        </p>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Nome</label>
          <input value={nome} onChange={e => setNome(e.target.value)} className="input-field w-full" required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">WhatsApp</label>
          <input
            value={whatsapp}
            onChange={e => setWhatsapp(formatWhatsapp(e.target.value))}
            className="input-field w-full"
            placeholder="Ex: 44 99948-9948"
            inputMode="numeric"
            required
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Tipo</label>
          <select value={tipo} onChange={e => setTipo(e.target.value)} className="input-field w-full">
            <option value="autonomo">Autônomo</option>
            <option value="imobiliaria">Vinculado a Imobiliária</option>
          </select>
        </div>
        {tipo === 'imobiliaria' && (
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Imobiliária</label>
            <select value={imobiliariaId} onChange={e => setImobiliariaId(e.target.value)} className="input-field w-full">
              <option value="">Selecione...</option>
              {(imobiliarias || []).map(i => <option key={i.id} value={i.id}>{i.nome}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Origem</label>
          <select value={origem} onChange={e => setOrigem(e.target.value)} className="input-field w-full">
            <option value="">Selecione...</option>
            {Object.entries(ORIGEM_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </div>
        {criarMut.isError && (
          <p className="text-sm text-red-600">{criarMut.error?.response?.data?.error || 'Erro ao criar lead'}</p>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
          <button type="submit" disabled={criarMut.isPending} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-secondary disabled:opacity-50">
            {criarMut.isPending ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// Anotação com data/hora automática — POST na mesma timeline de interações usada em
// ParceiroDetail.jsx (partner_signals pra cadastrados, corretor_lead_interacoes pra leads).
function AnotacaoModal({ isOpen, onClose, card }) {
  const qc = useQueryClient();
  const [descricao, setDescricao] = useState('');

  const salvarMut = useMutation({
    mutationFn: () => api.post(interacoesUrl(card), { tipo: 'nota_manual', descricao }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['parceiros-funil-corretores'] });
      onClose();
      setDescricao('');
    },
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Anotação — ${card?.nome || ''}`} size="sm">
      <form onSubmit={e => { e.preventDefault(); salvarMut.mutate(); }} className="space-y-3">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Anotação</label>
          <textarea
            value={descricao}
            onChange={e => setDescricao(e.target.value)}
            rows={4}
            className="input-field w-full resize-y"
            placeholder="O que aconteceu nesse contato..."
            required
          />
          <p className="text-xs text-gray-400 mt-1">A data e hora de agora são salvas automaticamente.</p>
        </div>
        {salvarMut.isError && (
          <p className="text-sm text-red-600">{salvarMut.error?.response?.data?.error || 'Erro ao salvar anotação'}</p>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
          <button type="submit" disabled={salvarMut.isPending} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-secondary disabled:opacity-50">
            {salvarMut.isPending ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// Grava um PTT curto direto do navegador — mesma técnica da tela Agendamentos
// (frontend/src/pages/Agendamentos/Agendamentos.jsx), reduzida ao essencial pra caber no modal.
function MiniAudioRecorder({ onAudioReady }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const mr = useRef(null); const chunks = useRef([]); const timer = useRef(null);
  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      chunks.current = [];
      rec.ondataavailable = e => { if (e.data.size > 0) chunks.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunks.current, { type: 'audio/webm' });
        onAudioReady({ arquivo: blob, preview: URL.createObjectURL(blob), nomeOriginal: `audio_${Date.now()}.webm` });
        stream.getTracks().forEach(t => t.stop());
      };
      rec.start(); mr.current = rec; setRecording(true); setSeconds(0);
      timer.current = setInterval(() => setSeconds(s => s + 1), 1000);
    } catch { alert('Microfone indisponível.'); }
  };
  const stop = () => { mr.current?.stop(); clearInterval(timer.current); setRecording(false); };
  return recording
    ? <button type="button" onClick={stop} className="flex items-center gap-2 px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs hover:bg-red-600"><StopCircle size={13} /> Parar ({seconds}s)</button>
    : <button type="button" onClick={start} className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs hover:bg-gray-100"><Mic size={13} /> Gravar áudio</button>;
}

// Agenda o próximo contato (data_proxima_acao) sem mudar o estágio — reaproveita o mesmo PATCH
// de estágio, só que reenviando o estagio_funil atual junto (o backend só loga "mudança de
// estágio" quando o valor realmente muda). Opcionalmente também cria um agendamento em
// `mensagens_agendadas` (mesma infra da tela Agendamentos/scheduler de 30s) pra disparar uma
// mensagem de texto ou áudio automática pro WhatsApp do contato na data/hora escolhida.
const MIDIA_ACCEPT = {
  audio: '.mp3,.ogg,.opus,.wav,.m4a,.webm',
  imagem: 'image/*',
  video: 'video/mp4,video/quicktime,video/*',
};

function AgendaModal({ isOpen, onClose, card }) {
  const qc = useQueryClient();
  const [data, setData] = useState(card?.data_proxima_acao ? card.data_proxima_acao.slice(0, 10) : '');
  const [msgAtiva, setMsgAtiva] = useState(false);
  const [msgTipo, setMsgTipo] = useState('texto');
  const [msgTexto, setMsgTexto] = useState('');
  const [msgMidia, setMsgMidia] = useState(null);
  const [msgLegenda, setMsgLegenda] = useState('');
  const [msgData, setMsgData] = useState('');
  const [msgInstancia, setMsgInstancia] = useState('');
  const [validacaoErro, setValidacaoErro] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setMsgAtiva(false); setMsgTipo('texto'); setMsgTexto(''); setMsgMidia(null); setMsgLegenda('');
      setMsgData(''); setValidacaoErro('');
    }
  }, [isOpen]);

  const { data: instanciasData } = useQuery({
    queryKey: ['evolution-instancias-funil'],
    queryFn: () => api.get('/envios-massa/instancias').then(r => r.data),
    enabled: isOpen,
    refetchOnWindowFocus: false,
  });
  const instancias = instanciasData?.data || [];
  useEffect(() => { if (instancias.length && !msgInstancia) setMsgInstancia(instancias[0].name); }, [instancias, msgInstancia]);

  const agendarMut = useMutation({
    mutationFn: async () => {
      await api.patch(estagioUrl(card), { estagio_funil: card.estagio_funil, data_proxima_acao: data || null });
      if (msgAtiva) {
        const fd = new FormData();
        const steps = msgTipo === 'texto'
          ? [{ tipo: 'texto', conteudo: msgTexto }]
          : [{ tipo: msgTipo, legenda: msgLegenda || '', nomeOriginal: msgMidia?.nomeOriginal || '' }];
        fd.append('config', JSON.stringify({
          nome_contato: card?.nome || '',
          telefone: card?.whatsapp || '',
          instancia_evolution: msgInstancia,
          agendar_para: msgData,
          observacao: `Agendado pelo funil de corretores — ${card?.nome || ''}`,
          steps,
        }));
        if (msgTipo !== 'texto' && msgMidia?.arquivo) {
          fd.append('step_0', msgMidia.arquivo, msgMidia.nomeOriginal || `${msgTipo}_${Date.now()}`);
        }
        await api.post('/agendamentos', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 30000 });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['parceiros-funil-corretores'] });
      onClose();
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setValidacaoErro('');
    if (msgAtiva) {
      if (!card?.whatsapp) return setValidacaoErro('Este contato não tem WhatsApp cadastrado — edite o contato antes.');
      if (!msgInstancia) return setValidacaoErro('Selecione a instância do WhatsApp.');
      if (!msgData) return setValidacaoErro('Informe a data e hora do envio automático.');
      if (new Date(msgData) <= new Date()) return setValidacaoErro('A data do envio automático deve ser no futuro.');
      if (msgTipo === 'texto' && !msgTexto.trim()) return setValidacaoErro('Escreva a mensagem de texto.');
      if (msgTipo !== 'texto' && !msgMidia) return setValidacaoErro(`Selecione ${msgTipo === 'audio' ? 'um áudio' : msgTipo === 'imagem' ? 'uma imagem' : 'um vídeo'}.`);
    }
    agendarMut.mutate();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Agendar próximo contato — ${card?.nome || ''}`} size="sm">
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Data combinada de retorno</label>
          <input type="date" value={data} onChange={e => setData(e.target.value)} className="input-field w-full" required />
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-600 pt-1 cursor-pointer">
          <input type="checkbox" checked={msgAtiva} onChange={e => setMsgAtiva(e.target.checked)} />
          Programar mensagem automática no WhatsApp
        </label>

        {msgAtiva && (
          <div className="border border-gray-200 rounded-lg p-3 space-y-3 bg-gray-50">
            {!card?.whatsapp && (
              <p className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-2 py-1.5">
                Este contato não tem WhatsApp cadastrado — edite o contato antes de programar o envio.
              </p>
            )}
            <div className="flex gap-1.5">
              {[['texto', 'Texto'], ['audio', 'Áudio'], ['imagem', 'Imagem'], ['video', 'Vídeo']].map(([tipo, label]) => (
                <button
                  key={tipo}
                  type="button"
                  onClick={() => { setMsgTipo(tipo); setMsgMidia(null); }}
                  className={`flex-1 py-1.5 text-xs rounded-lg border ${msgTipo === tipo ? 'bg-blue-50 border-blue-300 text-blue-700 font-semibold' : 'bg-white border-gray-200 text-gray-500'}`}
                >
                  {label}
                </button>
              ))}
            </div>

            {msgTipo === 'texto' ? (
              <textarea
                value={msgTexto}
                onChange={e => setMsgTexto(e.target.value)}
                placeholder="Digite a mensagem que será enviada automaticamente..."
                rows={3}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-y bg-white"
              />
            ) : (
              <div className="space-y-2">
                {msgMidia ? (
                  <div className="flex items-center gap-2">
                    {msgTipo === 'audio' && <audio src={msgMidia.preview} controls className="h-8 flex-1" />}
                    {msgTipo === 'imagem' && <img src={msgMidia.preview} alt="" className="h-16 w-16 object-cover rounded-lg border" />}
                    {msgTipo === 'video' && <video src={msgMidia.preview} className="h-16 w-24 object-cover rounded-lg border" />}
                    {msgTipo !== 'audio' && <span className="text-xs text-gray-500 truncate flex-1">{msgMidia.nomeOriginal}</span>}
                    <button type="button" onClick={() => setMsgMidia(null)} className="text-gray-400 hover:text-red-500"><X size={14} /></button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => fileRef.current?.click()} className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs hover:bg-gray-100"><Upload size={13} /> Arquivo</button>
                    {msgTipo === 'audio' && <MiniAudioRecorder onAudioReady={setMsgMidia} />}
                  </div>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept={MIDIA_ACCEPT[msgTipo]}
                  className="hidden"
                  onChange={e => {
                    const f = e.target.files[0];
                    if (!f) return;
                    setMsgMidia({ arquivo: f, preview: URL.createObjectURL(f), nomeOriginal: f.name });
                    e.target.value = '';
                  }}
                />
                {(msgTipo === 'imagem' || msgTipo === 'video') && msgMidia && (
                  <input
                    value={msgLegenda}
                    onChange={e => setMsgLegenda(e.target.value)}
                    placeholder="Legenda (opcional)"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                  />
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Enviar em</label>
                <input type="datetime-local" value={msgData} onChange={e => setMsgData(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Instância WhatsApp</label>
                {instancias.length === 0
                  ? <div className="text-xs text-gray-400 border border-gray-200 rounded-lg px-2 py-2 bg-white">Carregando...</div>
                  : (
                    <select value={msgInstancia} onChange={e => setMsgInstancia(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white">
                      {instancias.map(i => <option key={i.name} value={i.name}>{i.name}</option>)}
                    </select>
                  )}
              </div>
            </div>
          </div>
        )}

        {validacaoErro && <p className="text-sm text-red-600">{validacaoErro}</p>}
        {agendarMut.isError && (
          <p className="text-sm text-red-600">{agendarMut.error?.response?.data?.error || 'Erro ao agendar'}</p>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
          <button type="submit" disabled={agendarMut.isPending} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-secondary disabled:opacity-50">
            {agendarMut.isPending ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// Agenda uma visita de verdade — cria o booking no cal.diy (sincroniza com Google/Apple
// Agenda) e avança o card pro estágio "Visita Agendada", igual o AgendaModal faz pra
// "próximo contato", mas com data+hora+local em vez de só a data.
function AgendarVisitaModal({ isOpen, onClose, card }) {
  const qc = useQueryClient();
  const [data, setData] = useState('');
  const [hora, setHora] = useState('');
  const [local, setLocal] = useState('');
  const [observacao, setObservacao] = useState('');

  const agendarMut = useMutation({
    mutationFn: () => api.post(visitaUrl(card), { data, hora, local, observacao: observacao || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['parceiros-funil-corretores'] });
      onClose();
      setData(''); setHora(''); setLocal(''); setObservacao('');
    },
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Agendar visita — ${card?.nome || ''}`} size="sm">
      <form onSubmit={e => { e.preventDefault(); agendarMut.mutate(); }} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Data</label>
            <input type="date" value={data} onChange={e => setData(e.target.value)} className="input-field w-full" required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Hora</label>
            <input type="time" value={hora} onChange={e => setHora(e.target.value)} className="input-field w-full" required />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Local</label>
          <input
            value={local}
            onChange={e => setLocal(e.target.value)}
            className="input-field w-full"
            placeholder="Ex: Decorado Ingarden"
            required
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Observação</label>
          <textarea
            value={observacao}
            onChange={e => setObservacao(e.target.value)}
            rows={2}
            className="input-field w-full resize-y"
          />
        </div>
        <p className="text-xs text-gray-400">
          Isso cria o compromisso no cal.diy e sincroniza automaticamente com o Google Agenda e o Apple Agenda.
        </p>
        {agendarMut.isError && (
          agendarMut.error?.response?.data?.conflito ? (
            <div className="text-sm bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 space-y-1">
              <p className="font-semibold">Conflito de agenda</p>
              <p>
                Já existe um compromisso nesse horário
                {agendarMut.error.response.data.conflito.com ? <> com <strong>{agendarMut.error.response.data.conflito.com}</strong></> : ''}
                {agendarMut.error.response.data.conflito.local ? <> em <strong>{agendarMut.error.response.data.conflito.local}</strong></> : ''}.
              </p>
              <p className="text-xs text-red-500">
                {new Date(agendarMut.error.response.data.conflito.inicio).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                {' '}às{' '}
                {new Date(agendarMut.error.response.data.conflito.fim).toLocaleString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          ) : (
            <p className="text-sm text-red-600">{agendarMut.error?.response?.data?.error || 'Erro ao agendar visita'}</p>
          )
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
          <button type="submit" disabled={agendarMut.isPending} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-secondary disabled:opacity-50">
            {agendarMut.isPending ? 'Agendando...' : 'Agendar visita'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

const DISPARO_SHORTCODES = [
  { code: '#primeironome', desc: 'Primeiro nome do contato' },
  { code: '#nomecompleto', desc: 'Nome completo do contato' },
  { code: '#periodododia', desc: 'Bom dia / Boa tarde / Boa noite' },
  { code: '#diasemana', desc: 'Ex: Segunda-feira' },
];

function ShortcodeSelector({ onInsert }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block">
      <button type="button" onClick={() => setOpen(o => !o)} className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs font-medium hover:bg-blue-100">
        <span>#</span> Atalhos <ChevronDown size={11} />
      </button>
      {open && (
        <div className="absolute z-20 top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-56">
          {DISPARO_SHORTCODES.map(s => (
            <button key={s.code} type="button" onClick={() => { onInsert(s.code); setOpen(false); }} className="w-full text-left px-3 py-1.5 hover:bg-blue-50 flex items-center gap-2">
              <span className="text-xs font-mono text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">{s.code}</span>
              <span className="text-xs text-gray-500">{s.desc}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const ESTEIRA_STEP_ICONS = { texto: Type, imagem: Image, video: Video, audio: Music, documento: FileText };
const ESTEIRA_STEP_LABELS = { texto: 'Texto', imagem: 'Imagem', video: 'Vídeo', audio: 'Áudio', documento: 'Documento' };
const ESTEIRA_MIDIA_ACCEPT = {
  imagem: 'image/*',
  video: 'video/mp4,video/quicktime,video/*',
  audio: '.mp3,.ogg,.opus,.wav,.m4a,.webm',
  documento: '.pdf,.doc,.docx,.xls,.xlsx',
};

// Um bloco da esteira — texto, imagem, vídeo, áudio ou documento, na ordem em que é enviado
// pra cada contato. Reaproveita o mesmo motor de campanhas de EnviosMassa.jsx (POST
// /envios-massa com multipart, um arquivo por bloco em step_<indice>), só que os contatos vêm
// do funil/grupo/corretores em vez de Excel/segmento de engajamento.
function EsteiraStepCard({ step, index, total, onUpdate, onRemove, onMoveUp, onMoveDown }) {
  const fileRef = useRef(null);
  const textRef = useRef(null);
  const Icon = ESTEIRA_STEP_ICONS[step.tipo];
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    onUpdate({ ...step, arquivo: file, preview: URL.createObjectURL(file), nomeOriginal: file.name });
    e.target.value = '';
  };
  const insertShortcode = (code) => {
    const ta = textRef.current;
    const conteudo = step.conteudo || '';
    if (!ta) { onUpdate({ ...step, conteudo: conteudo + code }); return; }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const newText = conteudo.substring(0, start) + code + conteudo.substring(end);
    onUpdate({ ...step, conteudo: newText });
    setTimeout(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = start + code.length; }, 0);
  };
  return (
    <div className="border border-gray-200 rounded-xl p-3 bg-white space-y-2">
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5 bg-gray-100 px-2 py-1 rounded-lg">
          <Icon size={13} className="text-gray-600" />
          <span className="text-xs font-semibold text-gray-600">{index + 1}. {ESTEIRA_STEP_LABELS[step.tipo]}</span>
        </div>
        <div className="flex-1" />
        <button type="button" onClick={onMoveUp} disabled={index === 0} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><ArrowUp size={14} /></button>
        <button type="button" onClick={onMoveDown} disabled={index === total - 1} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><ArrowDown size={14} /></button>
        <button type="button" onClick={onRemove} disabled={total === 1} className="p-1 text-gray-400 hover:text-red-500 disabled:opacity-30"><Trash2 size={14} /></button>
      </div>

      {step.tipo === 'texto' && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <ShortcodeSelector onInsert={insertShortcode} />
            <span className="text-xs text-gray-400">Ex: #primeironome, #periodododia</span>
          </div>
          <textarea
            ref={textRef}
            value={step.conteudo || ''}
            onChange={e => onUpdate({ ...step, conteudo: e.target.value })}
            rows={3}
            placeholder="Olá #primeironome, #periodododia! tudo bem?"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-y"
          />
        </div>
      )}

      {step.tipo === 'imagem' && (
        <div className="space-y-2">
          {step.preview ? (
            <div className="flex items-center gap-3">
              <img src={step.preview} alt="" className="h-16 w-16 object-cover rounded-lg border" />
              <span className="text-xs text-gray-500 truncate flex-1">{step.nomeOriginal}</span>
              <button type="button" onClick={() => onUpdate({ ...step, arquivo: null, preview: null, nomeOriginal: '' })} className="text-gray-400 hover:text-red-500"><X size={14} /></button>
            </div>
          ) : (
            <button type="button" onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-gray-200 rounded-lg py-4 text-center text-sm text-gray-400 hover:border-blue-300 hover:text-blue-500">
              <Image size={20} className="mx-auto mb-1 opacity-50" />Selecionar imagem
            </button>
          )}
          <input ref={fileRef} type="file" accept={ESTEIRA_MIDIA_ACCEPT.imagem} className="hidden" onChange={handleFile} />
          {step.preview && (
            <input value={step.legenda || ''} onChange={e => onUpdate({ ...step, legenda: e.target.value })} placeholder="Legenda (opcional)" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          )}
        </div>
      )}

      {step.tipo === 'video' && (
        <div className="space-y-2">
          {step.preview ? (
            <div className="flex items-center gap-3">
              <video src={step.preview} className="h-16 w-24 object-cover rounded-lg border" />
              <span className="text-xs text-gray-500 truncate flex-1">{step.nomeOriginal}</span>
              <button type="button" onClick={() => onUpdate({ ...step, arquivo: null, preview: null, nomeOriginal: '' })} className="text-gray-400 hover:text-red-500"><X size={14} /></button>
            </div>
          ) : (
            <button type="button" onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-gray-200 rounded-lg py-4 text-center text-sm text-gray-400 hover:border-blue-300 hover:text-blue-500">
              <Video size={20} className="mx-auto mb-1 opacity-50" />Selecionar vídeo
            </button>
          )}
          <input ref={fileRef} type="file" accept={ESTEIRA_MIDIA_ACCEPT.video} className="hidden" onChange={handleFile} />
          {step.preview && (
            <input value={step.legenda || ''} onChange={e => onUpdate({ ...step, legenda: e.target.value })} placeholder="Legenda (opcional)" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          )}
        </div>
      )}

      {step.tipo === 'audio' && (
        <div className="space-y-2">
          {step.preview ? (
            <div className="flex items-center gap-3">
              <audio src={step.preview} controls className="h-8 flex-1" />
              <span className="text-xs text-gray-500 truncate max-w-[120px]">{step.nomeOriginal}</span>
              <button type="button" onClick={() => onUpdate({ ...step, arquivo: null, preview: null, nomeOriginal: '' })} className="text-gray-400 hover:text-red-500"><X size={14} /></button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => fileRef.current?.click()} className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs hover:bg-gray-200"><Upload size={13} /> Arquivo</button>
              <MiniAudioRecorder onAudioReady={({ arquivo, preview, nomeOriginal }) => onUpdate({ ...step, arquivo, preview, nomeOriginal })} />
            </div>
          )}
          <input ref={fileRef} type="file" accept={ESTEIRA_MIDIA_ACCEPT.audio} className="hidden" onChange={handleFile} />
        </div>
      )}

      {step.tipo === 'documento' && (
        <div className="space-y-2">
          {step.preview ? (
            <div className="flex items-center gap-3">
              <FileText size={20} className="text-gray-400" />
              <span className="text-xs text-gray-600 truncate flex-1">{step.nomeOriginal}</span>
              <button type="button" onClick={() => onUpdate({ ...step, arquivo: null, preview: null, nomeOriginal: '' })} className="text-gray-400 hover:text-red-500"><X size={14} /></button>
            </div>
          ) : (
            <button type="button" onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-gray-200 rounded-lg py-4 text-center text-sm text-gray-400 hover:border-blue-300 hover:text-blue-500">
              <FileText size={20} className="mx-auto mb-1 opacity-50" />Selecionar documento
            </button>
          )}
          <input ref={fileRef} type="file" accept={ESTEIRA_MIDIA_ACCEPT.documento} className="hidden" onChange={handleFile} />
        </div>
      )}
    </div>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}
function formatTelefone(phone) {
  const d = String(phone || '').replace(/\D/g, '');
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return phone;
}
const CAMPANHA_STATUS_CONFIG = {
  pendente: { label: 'Pendente', color: 'bg-gray-100 text-gray-600' },
  na_fila: { label: 'Na fila', color: 'bg-indigo-100 text-indigo-700' },
  agendada: { label: 'Agendada', color: 'bg-purple-100 text-purple-700' },
  em_andamento: { label: 'Em andamento', color: 'bg-blue-100 text-blue-700' },
  pausada: { label: 'Pausada', color: 'bg-yellow-100 text-yellow-700' },
  concluida: { label: 'Concluída', color: 'bg-green-100 text-green-700' },
  cancelada: { label: 'Cancelada', color: 'bg-red-100 text-red-700' },
  erro: { label: 'Erro', color: 'bg-red-100 text-red-700' },
};
function CampanhaStatusBadge({ status }) {
  const cfg = CAMPANHA_STATUS_CONFIG[status] || CAMPANHA_STATUS_CONFIG.pendente;
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cfg.color}`}>{cfg.label}</span>;
}

// Histórico das campanhas criadas pelo botão "Mensagens automáticas" (origem=funil_corretores),
// separado do histórico geral de EnviosMassa.jsx. Mesmo padrão de lista + detalhe + ações.
function CampanhasFunilHistorico() {
  const qc = useQueryClient();
  const [detalheId, setDetalheId] = useState(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['campanhas-massa-funil'],
    queryFn: () => api.get('/envios-massa', { params: { origem: 'funil_corretores' } }).then(r => r.data),
    refetchInterval: detalheId ? 3000 : 5000,
  });
  const { data: instanciasMetaData } = useQuery({
    queryKey: ['envios-massa-instancias-meta'],
    queryFn: () => api.get('/envios-massa/instancias-meta').then(r => r.data),
    refetchOnWindowFocus: false,
  });
  const nomesMetaPorId = new Map((instanciasMetaData?.data || []).map(n => [n.id, n.nome]));
  const labelInstancia = (c) => c.instancia_tipo === 'meta_oficial'
    ? `Meta: ${nomesMetaPorId.get(c.instancia_evolution) || c.instancia_evolution}`
    : c.instancia_evolution;
  const { data: detalheData } = useQuery({
    queryKey: ['campanha-detalhe', detalheId],
    queryFn: () => detalheId ? api.get(`/envios-massa/${detalheId}`).then(r => r.data) : null,
    enabled: !!detalheId,
    refetchInterval: 3000,
  });

  const campanhas = data?.data || [];
  const detalhe = detalheData?.data || null;

  const acao = async (id, action) => {
    try {
      if (action === 'cancelar') await api.delete(`/envios-massa/${id}`);
      else await api.post(`/envios-massa/${id}/${action}`);
      qc.invalidateQueries({ queryKey: ['campanhas-massa-funil'] });
      qc.invalidateQueries({ queryKey: ['campanha-detalhe', id] });
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  if (isLoading) return <div className="py-10 text-center text-gray-400 text-sm">Carregando...</div>;

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button onClick={() => refetch()} className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-500 hover:bg-gray-50">
          <RefreshCw size={12} /> Atualizar
        </button>
      </div>
      {campanhas.length === 0 ? (
        <div className="py-10 text-center text-gray-400 text-sm">Nenhum disparo feito por aqui ainda.</div>
      ) : (
        <div className="border border-gray-100 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-2.5 text-left">Campanha</th>
                <th className="px-3 py-2.5 text-left">Instância</th>
                <th className="px-3 py-2.5 text-left">Status</th>
                <th className="px-3 py-2.5 text-center">Progresso</th>
                <th className="px-3 py-2.5 text-left">Data</th>
                <th className="px-3 py-2.5 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {campanhas.map(c => {
                const pct = c.total > 0 ? Math.round((c.enviados / c.total) * 100) : 0;
                const aberta = detalheId === c.id;
                return (
                  <React.Fragment key={c.id}>
                    <tr className={`border-t border-gray-50 hover:bg-gray-50 ${aberta ? 'bg-blue-50' : ''}`}>
                      <td className="px-4 py-2.5 font-medium text-gray-800">{c.nome}</td>
                      <td className="px-3 py-2.5 text-gray-500 text-xs">{labelInstancia(c)}</td>
                      <td className="px-3 py-2.5">
                        <CampanhaStatusBadge status={c.status} />
                        {c.status === 'agendada' && c.agendado_para && (
                          <p className="text-xs text-gray-400 mt-0.5 whitespace-nowrap">{fmtDate(c.agendado_para)}</p>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2 justify-center">
                          <div className="w-20 bg-gray-200 rounded-full h-1.5"><div className="bg-green-500 h-1.5 rounded-full" style={{ width: `${pct}%` }} /></div>
                          <span className="text-xs text-gray-500 whitespace-nowrap">{c.enviados}/{c.total}{c.erros > 0 && <span className="text-red-400 ml-1">({c.erros} err)</span>}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-gray-400">{fmtDate(c.criado_em)}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1.5 justify-end">
                          {c.status === 'pendente' && (
                            <button onClick={() => acao(c.id, 'iniciar')} className="flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded text-xs font-medium hover:bg-green-100"><Play size={11} /> Iniciar</button>
                          )}
                          {c.status === 'pausada' && (
                            <button onClick={() => acao(c.id, 'retomar')} className="flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded text-xs font-medium hover:bg-green-100"><Play size={11} /> Retomar</button>
                          )}
                          {c.status === 'agendada' && (
                            <button onClick={() => { if (window.confirm('Disparar agora, sem esperar a data agendada?')) acao(c.id, 'iniciar'); }} className="flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded text-xs font-medium hover:bg-green-100"><Play size={11} /> Disparar agora</button>
                          )}
                          {c.status === 'em_andamento' && (
                            <button onClick={() => acao(c.id, 'pausar')} className="flex items-center gap-1 px-2 py-1 bg-yellow-50 text-yellow-700 rounded text-xs font-medium hover:bg-yellow-100"><Pause size={11} /> Pausar</button>
                          )}
                          {c.status === 'na_fila' && (
                            <>
                              <span className="text-xs text-gray-400 px-1">Na fila da instância</span>
                              <button onClick={() => acao(c.id, 'retomar')} className="flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded text-xs font-medium hover:bg-green-100"><Play size={11} /> Começar agora</button>
                            </>
                          )}
                          {['em_andamento', 'pausada', 'pendente', 'agendada', 'na_fila'].includes(c.status) && (
                            <button onClick={() => { if (window.confirm(c.status === 'agendada' ? 'Cancelar esse agendamento?' : 'Cancelar esse disparo? Os contatos que ainda não receberam mensagem ficam de fora.')) acao(c.id, 'cancelar'); }} className="flex items-center gap-1 px-2 py-1 bg-red-50 text-red-700 rounded text-xs font-medium hover:bg-red-100"><X size={11} /> Cancelar</button>
                          )}
                          <button onClick={() => setDetalheId(aberta ? null : c.id)} className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium hover:bg-gray-200">
                            {aberta ? <ChevronUp size={11} /> : <ChevronDown size={11} />} Relatório
                          </button>
                        </div>
                      </td>
                    </tr>
                    {aberta && detalhe && (
                      <tr>
                        <td colSpan={6} className="bg-blue-50 px-4 py-3">
                          <div className="space-y-2">
                            <div className="flex items-center gap-4 text-xs text-gray-600">
                              <span>Total: <strong>{detalhe.total}</strong></span>
                              <span className="text-green-700">Enviados: <strong>{detalhe.enviados}</strong></span>
                              <span className="text-red-600">Erros: <strong>{detalhe.erros}</strong></span>
                            </div>
                            <div className="overflow-auto max-h-48 rounded-lg border border-blue-100">
                              <table className="w-full text-xs">
                                <thead className="bg-blue-100 sticky top-0">
                                  <tr>
                                    <th className="px-3 py-1.5 text-left">Nome</th>
                                    <th className="px-3 py-1.5 text-left">Telefone</th>
                                    <th className="px-3 py-1.5 text-left">Status</th>
                                    <th className="px-3 py-1.5 text-left">Enviado</th>
                                    <th className="px-3 py-1.5 text-left">Erro</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(detalhe.contatos || []).map(ct => (
                                    <tr key={ct.id} className="border-t border-blue-100 hover:bg-blue-50">
                                      <td className="px-3 py-1.5">{ct.nome || '—'}</td>
                                      <td className="px-3 py-1.5 font-mono">{formatTelefone(ct.telefone)}</td>
                                      <td className="px-3 py-1.5">
                                        <span className={ct.status === 'enviado' ? 'text-green-600' : ct.status === 'erro' ? 'text-red-600' : 'text-gray-400'}>{ct.status}</span>
                                      </td>
                                      <td className="px-3 py-1.5 text-gray-400">{fmtDate(ct.enviado_em)}</td>
                                      <td className="px-3 py-1.5 text-red-500 truncate max-w-[200px]" title={ct.erro_msg}>{ct.erro_msg || ''}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Também usado pelo Marketing › Criar Imagens (imagem + legenda): initialSteps pré-carrega a esteira; origem/prefixoNome identificam a campanha no histórico.
export function DisparoMassaModal({ isOpen, onClose, kanban, estagios, initialSteps, origem = 'funil_corretores', prefixoNome = 'Funil corretores', titulo = 'Mensagens automáticas — Funil de Leads Corretores' }) {
  const qc = useQueryClient();
  const [tab, setTab] = useState('nova');
  // Conjunto de origens ativas — o disparo pode combinar várias fontes ao mesmo tempo
  // (ex: coluna do funil + corretores cadastrados), os contatos são mesclados e deduplicados
  // por telefone antes do envio.
  const [origensAtivas, setOrigensAtivas] = useState(new Set(['funil'])); // subconjunto de 'funil' | 'grupo' | 'corretores' | 'sem_grupo' | 'grupos_destino' | 'agenda' | 'eventos' | 'lista_manual'
  const toggleOrigem = (origem) => {
    setOrigensAtivas(prev => {
      const next = new Set(prev);
      if (next.has(origem)) next.delete(origem); else next.add(origem);
      return next;
    });
  };
  const [estagiosAtivos, setEstagiosAtivos] = useState(new Set()); // colunas do funil selecionadas (pode ser mais de uma)
  const [selecionados, setSelecionados] = useState(new Set());
  const [grupoSelecionado, setGrupoSelecionado] = useState('');
  const [selecionadosGrupo, setSelecionadosGrupo] = useState(new Set());
  const [gruposDestinoSelecionados, setGruposDestinoSelecionados] = useState(new Set());
  const [selecionadosCorretores, setSelecionadosCorretores] = useState(new Set());
  const [buscaCorretores, setBuscaCorretores] = useState('');
  const [selecionadosSemGrupo, setSelecionadosSemGrupo] = useState(new Set()); // origem "sem_grupo": chave = sufixo de 8 díg
  const [buscaSemGrupo, setBuscaSemGrupo] = useState('');
  const [selecionadosAgenda, setSelecionadosAgenda] = useState(new Set());
  const [buscaAgenda, setBuscaAgenda] = useState('');
  const [eventoSelecionado, setEventoSelecionado] = useState('');
  const [selecionadosEventos, setSelecionadosEventos] = useState(new Set());
  const [buscaEventos, setBuscaEventos] = useState('');
  const [filtroDiaEventos, setFiltroDiaEventos] = useState(''); // '' = todas as datas
  const [filtroVinculoEvento, setFiltroVinculoEvento] = useState(''); // '' = todos | 'autonomo' | 'imob:<nome>'
  const [listaManualTexto, setListaManualTexto] = useState(''); // origem "lista manual": números colados à mão
  const [excelManualLoading, setExcelManualLoading] = useState(false);
  const excelManualInputRef = useRef(null);
  const [steps, setSteps] = useState([{ tipo: 'texto', conteudo: '', arquivo: null, preview: null, nomeOriginal: '', legenda: '' }]);
  useEffect(() => { if (isOpen && initialSteps?.length) setSteps(initialSteps); }, [isOpen]);
  const [provedor, setProvedor] = useState('evolution'); // 'evolution' | 'meta_oficial'
  const [instancia, setInstancia] = useState('');
  const [instanciaMeta, setInstanciaMeta] = useState('');
  const [delaySeg, setDelaySeg] = useState(5);
  const [delayMensagensSeg, setDelayMensagensSeg] = useState(15);
  const [pularEnviadosHoje, setPularEnviadosHoje] = useState(false);
  const [pularGestores, setPularGestores] = useState(false);
  const [agendamentoAtivo, setAgendamentoAtivo] = useState(false);
  const [agendarPara, setAgendarPara] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  // Template aprovado da Meta — obrigatório pra iniciar conversa fora da janela de 24h
  const [modelosMeta, setModelosMeta] = useState([]);
  const [modeloMeta, setModeloMeta] = useState('');
  const [modeloHeaderTipo, setModeloHeaderTipo] = useState(null); // 'IMAGE' | 'VIDEO' | null
  const [metaHeaderFile, setMetaHeaderFile] = useState(null);
  const [loadingModelos, setLoadingModelos] = useState(false);
  const [erroModelos, setErroModelos] = useState('');
  const metaHeaderRef = useRef(null);
  const [eventoConfirmacaoId, setEventoConfirmacaoId] = useState('');
  const usesTemplate = provedor === 'meta_oficial' && !!modeloMeta;

  const { data: eventosTemplateData } = useQuery({
    queryKey: ['eventos-amarrar-template'],
    queryFn: () => api.get('/eventos').then(r => r.data),
    enabled: isOpen && usesTemplate,
    refetchOnWindowFocus: false,
  });
  const eventosAtivosTemplate = (eventosTemplateData?.data || []).filter(e => e.status === 'ativo');

  const { data: instanciasData } = useQuery({
    queryKey: ['evolution-instancias'],
    queryFn: () => api.get('/envios-massa/instancias').then(r => r.data),
    enabled: isOpen,
    refetchOnWindowFocus: false,
  });
  const instancias = instanciasData?.data || [];

  const { data: instanciasMetaData } = useQuery({
    queryKey: ['envios-massa-instancias-meta'],
    queryFn: () => api.get('/envios-massa/instancias-meta').then(r => r.data),
    enabled: isOpen,
    refetchOnWindowFocus: false,
  });
  const instanciasMeta = instanciasMetaData?.data || [];

  useEffect(() => {
    if (instancias.length && !instancia) setInstancia(instancias[0].name);
  }, [instancias, instancia]);

  // API Oficial Meta não tem como listar grupos nem "agenda sincronizada" (isso é coisa do
  // Baileys/Evolution) — trocar pra ela desmarca essas origens se estiverem ativas.
  const trocarProvedor = (p) => {
    setProvedor(p);
    if (p === 'meta_oficial') {
      setOrigensAtivas(prev => {
        const next = new Set(prev);
        next.delete('grupo'); next.delete('grupos_destino'); next.delete('agenda');
        return next;
      });
    } else {
      setModelosMeta([]); setModeloMeta(''); setModeloHeaderTipo(null); setMetaHeaderFile(null); setErroModelos('');
    }
  };

  // Modelos aprovados da conta Meta — carregados ao escolher o número da API Oficial. Pela API
  // Oficial, quem não respondeu nas últimas 24h só recebe mensagem iniciada por template.
  useEffect(() => {
    if (!isOpen || provedor !== 'meta_oficial' || !instanciaMeta) {
      setModelosMeta([]); setModeloMeta(''); setModeloHeaderTipo(null); setErroModelos('');
      return;
    }
    let cancelado = false;
    setLoadingModelos(true); setErroModelos(''); setModelosMeta([]); setModeloMeta(''); setModeloHeaderTipo(null); setMetaHeaderFile(null);
    api.get(`/whatsapp-oficial/${instanciaMeta}/modelos`)
      .then(r => {
        if (cancelado) return;
        const lista = r.data.data || [];
        setModelosMeta(lista);
        // Só um modelo aprovado na conta = já deixa selecionado (é "o modelo padrão").
        if (lista.length === 1) { setModeloMeta(lista[0].name); setModeloHeaderTipo(lista[0].header_type || null); }
      })
      .catch(err => { if (!cancelado) setErroModelos(err.response?.data?.error || 'Erro ao buscar modelos aprovados da Meta'); })
      .finally(() => { if (!cancelado) setLoadingModelos(false); });
    return () => { cancelado = true; };
  }, [isOpen, provedor, instanciaMeta]);

  // Mescla os contatos de todas as colunas do funil selecionadas (dedupe por tipo:id, já que
  // uma pessoa não deveria estar em dois estágios ao mesmo tempo, mas por segurança).
  const contatosColunaMap = new Map();
  for (const chave of estagiosAtivos) {
    for (const c of (kanban[chave] || [])) {
      if (!c.whatsapp) continue;
      contatosColunaMap.set(`${c.tipo}:${c.id}`, c);
    }
  }
  const contatosColuna = Array.from(contatosColunaMap.values());

  const toggleEstagio = (chave) => {
    const estavaAtiva = estagiosAtivos.has(chave);
    setEstagiosAtivos(prev => {
      const next = new Set(prev);
      if (estavaAtiva) next.delete(chave); else next.add(chave);
      return next;
    });
    // Ao ativar uma coluna, seleciona todos os contatos dela por padrão; ao desativar, remove
    // os contatos dela da seleção (senão ficariam "presos" marcados sem aparecer na lista).
    const contatosDaColuna = (kanban[chave] || []).filter(c => c.whatsapp);
    setSelecionados(prev => {
      const next = new Set(prev);
      contatosDaColuna.forEach(c => {
        const key = `${c.tipo}:${c.id}`;
        if (estavaAtiva) next.delete(key); else next.add(key);
      });
      return next;
    });
  };

  const toggleContato = (key) => {
    setSelecionados(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const marcarTodos = () => setSelecionados(new Set(contatosColuna.map(c => `${c.tipo}:${c.id}`)));
  const desmarcarTodos = () => setSelecionados(new Set());

  const contatosFunil = contatosColuna
    .filter(c => selecionados.has(`${c.tipo}:${c.id}`))
    .map((c, i) => ({
      nome: c.nome,
      telefone: c.whatsapp,
      corretor_id: c.tipo === 'parceiro' ? c.id : null,
      ordem: i,
    }));

  // Lista de grupos da instância selecionada — reaproveitada tanto pra "Grupo do WhatsApp"
  // (extrai os contatos do grupo pra disparo individual) quanto pra "Enviar direto pra grupo(s)"
  // (dispara a mensagem uma vez, direto no grupo, sem extrair ninguém).
  const { data: gruposData, isLoading: gruposLoading, error: gruposError } = useQuery({
    queryKey: ['evolution-grupos', instancia],
    queryFn: () => api.get('/envios-massa/grupos', { params: { instancia } }).then(r => r.data),
    enabled: isOpen && provedor === 'evolution' && (origensAtivas.has('grupo') || origensAtivas.has('grupos_destino')) && !!instancia,
    refetchOnWindowFocus: false,
  });
  const grupos = gruposData?.data || [];

  const { data: participantesData, isLoading: participantesLoading, error: participantesError } = useQuery({
    queryKey: ['evolution-grupo-participantes', instancia, grupoSelecionado],
    queryFn: () => api.get('/envios-massa/grupos/participantes', { params: { instancia, groupJid: grupoSelecionado } }).then(r => r.data),
    enabled: isOpen && provedor === 'evolution' && origensAtivas.has('grupo') && !!instancia && !!grupoSelecionado,
    refetchOnWindowFocus: false,
  });
  const participantesGrupo = participantesData?.data || [];

  useEffect(() => {
    setSelecionadosGrupo(new Set(participantesGrupo.map(p => p.telefone)));
  }, [participantesData]);

  const toggleContatoGrupo = (telefone) => {
    setSelecionadosGrupo(prev => {
      const next = new Set(prev);
      if (next.has(telefone)) next.delete(telefone); else next.add(telefone);
      return next;
    });
  };
  const marcarTodosGrupo = () => setSelecionadosGrupo(new Set(participantesGrupo.map(p => p.telefone)));
  const desmarcarTodosGrupo = () => setSelecionadosGrupo(new Set());

  const contatosGrupo = participantesGrupo
    .filter(p => selecionadosGrupo.has(p.telefone))
    .map((p, i) => ({ nome: p.nome, telefone: p.telefone, corretor_id: null, ordem: i }));

  // "Enviar direto pra grupo(s)": a mensagem vai uma vez pro chat do grupo (não extrai membro
  // nenhum) — o JID do grupo (ex: 120363...@g.us) vira o próprio "telefone" de destino. O
  // service.formatPhone já reconhece JID completo e não mexe nele.
  const toggleGrupoDestino = (id) => {
    setGruposDestinoSelecionados(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const marcarTodosGruposDestino = () => setGruposDestinoSelecionados(new Set(grupos.map(g => g.id)));
  const desmarcarTodosGruposDestino = () => setGruposDestinoSelecionados(new Set());

  const contatosGruposDestino = grupos
    .filter(g => gruposDestinoSelecionados.has(g.id))
    .map((g, i) => ({ nome: g.subject, telefone: g.id, corretor_id: null, ordem: i }));

  // Corretores já cadastrados no sistema (aba "Corretores" de Vendas → Parceiros, GET /parceiros),
  // independente de estarem em algum estágio do funil — terceira origem de contatos do disparo.
  const { data: corretoresData, isLoading: corretoresLoading, error: corretoresError } = useQuery({
    queryKey: ['parceiros-corretores-disparo'],
    queryFn: () => api.get('/parceiros').then(r => r.data),
    // Também alimenta a origem "sem_grupo" (universo = funil + corretores cadastrados).
    enabled: isOpen && (origensAtivas.has('corretores') || origensAtivas.has('sem_grupo')),
    refetchOnWindowFocus: false,
  });
  const corretoresComWhatsapp = (corretoresData?.data || []).filter(c => c.whatsapp);

  useEffect(() => {
    if (corretoresData) setSelecionadosCorretores(new Set(corretoresComWhatsapp.map(c => c.id)));
    // eslint-disable-next-line
  }, [corretoresData]);

  const corretoresFiltrados = corretoresComWhatsapp.filter(c => {
    const termo = buscaCorretores.trim().toLowerCase();
    if (!termo) return true;
    return c.nome?.toLowerCase().includes(termo) || c.imobiliaria_nome?.toLowerCase().includes(termo);
  });

  const toggleContatoCorretor = (id) => {
    setSelecionadosCorretores(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const marcarTodosCorretores = () => setSelecionadosCorretores(new Set(corretoresFiltrados.map(c => c.id)));
  const desmarcarTodosCorretores = () => setSelecionadosCorretores(new Set());

  const contatosCorretores = corretoresComWhatsapp
    .filter(c => selecionadosCorretores.has(c.id))
    .map((c, i) => ({ nome: c.nome, telefone: c.whatsapp, corretor_id: c.id, ordem: i }));

  // Origem "Não fazem parte de grupos": universo = TODOS os leads do funil + TODOS os corretores
  // cadastrados, MENOS quem participa de algum grupo "inBase" (instância fernando, cruzada no
  // backend — GET /envios-massa/grupos/inbase-telefones). Match pelos últimos 8 dígitos, mesma
  // chave de deduplicação usada no resto do sistema.
  const { data: inbaseGruposData, isLoading: inbaseGruposLoading, error: inbaseGruposError } = useQuery({
    queryKey: ['evolution-grupos-inbase-telefones'],
    queryFn: () => api.get('/envios-massa/grupos/inbase-telefones').then(r => r.data),
    enabled: isOpen && origensAtivas.has('sem_grupo'),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });
  const telefonesEmGrupo = new Set(inbaseGruposData?.data?.telefones8 || []);
  const gruposInbaseVerificados = inbaseGruposData?.data?.total_grupos || 0;

  // universoSemGrupo: um item por pessoa (dedupe por sufixo de 8 díg), com a chave `k`.
  const universoSemGrupoMap = new Map();
  const addUniverso = (nome, tel, corretor_id) => {
    const dig = String(tel || '').replace(/\D/g, '');
    if (dig.length < 10) return;
    const k = dig.slice(-8);
    if (!universoSemGrupoMap.has(k)) universoSemGrupoMap.set(k, { k, nome: nome || '', telefone: dig, corretor_id: corretor_id || null });
  };
  if (origensAtivas.has('sem_grupo')) {
    Object.values(kanban).forEach(cards => (cards || []).forEach(c => { if (c.whatsapp) addUniverso(c.nome, c.whatsapp, c.tipo === 'parceiro' ? c.id : null); }));
    (corretoresData?.data || []).forEach(c => { if (c.whatsapp) addUniverso(c.nome, c.whatsapp, c.id); });
  }
  const universoSemGrupo = Array.from(universoSemGrupoMap.values());
  const contatosSemGrupoDisponiveis = universoSemGrupo.filter(c => !telefonesEmGrupo.has(c.k));
  const excluidosPorGrupo = universoSemGrupo.length - contatosSemGrupoDisponiveis.length;

  useEffect(() => {
    if (inbaseGruposData) setSelecionadosSemGrupo(new Set(contatosSemGrupoDisponiveis.map(c => c.k)));
    // eslint-disable-next-line
  }, [inbaseGruposData, corretoresData]);

  const buscaSemGrupoTermo = buscaSemGrupo.trim().toLowerCase();
  const contatosSemGrupoFiltrados = contatosSemGrupoDisponiveis.filter(c =>
    !buscaSemGrupoTermo || c.nome.toLowerCase().includes(buscaSemGrupoTermo) || c.telefone.includes(buscaSemGrupoTermo)
  );
  const toggleContatoSemGrupo = (k) => {
    setSelecionadosSemGrupo(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };
  const marcarTodosSemGrupo = () => setSelecionadosSemGrupo(new Set(contatosSemGrupoFiltrados.map(c => c.k)));
  const desmarcarTodosSemGrupo = () => setSelecionadosSemGrupo(new Set());

  const contatosSemGrupo = contatosSemGrupoDisponiveis
    .filter(c => selecionadosSemGrupo.has(c.k))
    .map((c, i) => ({ nome: c.nome, telefone: c.telefone, corretor_id: c.corretor_id, ordem: i }));

  // Agenda de contatos sincronizada pela instância (quem já trocou mensagem direta com ela) —
  // quarta origem de contatos. Diferente de "grupo"/"corretores", NÃO seleciona tudo por padrão
  // ao carregar: a lista pode vir grande e cheia de gente fora do contexto do disparo, então o
  // usuário precisa marcar explicitamente (ou usar "Marcar todos" depois de filtrar pela busca).
  const { data: agendaData, isLoading: agendaLoading, error: agendaError } = useQuery({
    queryKey: ['evolution-contatos-agenda', instancia],
    queryFn: () => api.get('/envios-massa/contatos', { params: { instancia } }).then(r => r.data),
    enabled: isOpen && provedor === 'evolution' && origensAtivas.has('agenda') && !!instancia,
    refetchOnWindowFocus: false,
  });
  const contatosAgendaBrutos = agendaData?.data || [];

  const buscaAgendaTermo = buscaAgenda.trim().toLowerCase();
  const contatosAgendaFiltrados = contatosAgendaBrutos.filter(c =>
    !buscaAgendaTermo || c.nome?.toLowerCase().includes(buscaAgendaTermo) || c.telefone.includes(buscaAgendaTermo)
  );

  const toggleContatoAgenda = (telefone) => {
    setSelecionadosAgenda(prev => {
      const next = new Set(prev);
      if (next.has(telefone)) next.delete(telefone); else next.add(telefone);
      return next;
    });
  };
  const marcarTodosAgenda = () => setSelecionadosAgenda(prev => new Set([...prev, ...contatosAgendaFiltrados.map(c => c.telefone)]));
  const desmarcarTodosAgenda = () => setSelecionadosAgenda(prev => {
    const filtradosSet = new Set(contatosAgendaFiltrados.map(c => c.telefone));
    return new Set([...prev].filter(t => !filtradosSet.has(t)));
  });

  const contatosAgenda = contatosAgendaBrutos
    .filter(c => selecionadosAgenda.has(c.telefone))
    .map((c, i) => ({ nome: c.nome, telefone: c.telefone, corretor_id: null, ordem: i }));

  // Inscritos em Eventos (Parcerias → Eventos, ex: open house) — sexta origem de contatos.
  // Lista os eventos pra escolher qual, depois busca os confirmados daquele evento específico.
  const { data: eventosListaData } = useQuery({
    queryKey: ['eventos-disparo-lista'],
    queryFn: () => api.get('/eventos').then(r => r.data),
    enabled: isOpen && origensAtivas.has('eventos'),
    refetchOnWindowFocus: false,
  });
  const eventosDisponiveis = eventosListaData?.data || [];

  const { data: eventoDetalheData, isLoading: eventoDetalheLoading, error: eventoDetalheError } = useQuery({
    queryKey: ['evento-disparo-detalhe', eventoSelecionado],
    queryFn: () => api.get(`/eventos/${eventoSelecionado}`).then(r => r.data),
    enabled: isOpen && origensAtivas.has('eventos') && !!eventoSelecionado,
    refetchOnWindowFocus: false,
  });
  // Como confirmados de eventos representam interesse concreto num compromisso marcado,
  // seleciona todo mundo por padrão ao trocar de evento (mesmo padrão de "corretores").
  const confirmadosEvento = (eventoDetalheData?.data?.confirmacoes || []).filter(c => c.status !== 'cancelado' && c.whatsapp);

  useEffect(() => {
    setSelecionadosEventos(new Set(confirmadosEvento.map(c => c.id)));
    setBuscaEventos('');
    setFiltroDiaEventos('');
    setFiltroVinculoEvento('');
    // eslint-disable-next-line
  }, [eventoDetalheData]);

  const toggleContatoEvento = (id) => {
    setSelecionadosEventos(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Datas distintas de agendamento dentro do evento (ex: dia 28 e dia 29 do open house) — permite
  // separar o disparo por data, já que cada dia costuma levar um texto diferente.
  const diasEventoMap = new Map();
  for (const c of confirmadosEvento) {
    const chave = c.dia ? String(c.dia).slice(0, 10) : '__sem_data__';
    diasEventoMap.set(chave, (diasEventoMap.get(chave) || 0) + 1);
  }
  const diasEvento = Array.from(diasEventoMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([chave, total]) => ({
      chave,
      total,
      label: chave === '__sem_data__' ? 'Sem data' : new Date(`${chave}T00:00:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
    }));

  // Vínculo do inscrito (autônomo x imobiliária). `imobiliaria_nome` é texto livre e vem escrito
  // de vários jeitos — agrupa pela versão normalizada (ver normalizaImob) e usa como rótulo a
  // grafia original mais frequente. Sem imobiliária = "Autônomos / sem imobiliária".
  const vinculosEventoMap = new Map(); // chaveNorm -> { chave, label, total, _grafias: Map }
  let autonomosEventoTotal = 0;
  for (const c of confirmadosEvento) {
    const orig = (c.imobiliaria_nome || '').trim();
    const norm = normalizaImob(orig);
    if (orig && norm) {
      let cur = vinculosEventoMap.get(norm);
      if (!cur) { cur = { chave: `imob:${norm}`, label: orig, total: 0, _grafias: new Map() }; vinculosEventoMap.set(norm, cur); }
      cur.total += 1;
      cur._grafias.set(orig, (cur._grafias.get(orig) || 0) + 1);
    } else {
      autonomosEventoTotal += 1;
    }
  }
  for (const v of vinculosEventoMap.values()) {
    v.label = Array.from(v._grafias.entries()).sort((a, b) => b[1] - a[1] || b[0].length - a[0].length)[0][0];
    delete v._grafias;
  }
  const vinculosEvento = Array.from(vinculosEventoMap.values()).sort((a, b) => b.total - a.total || a.label.localeCompare(b.label));
  const temFiltroVinculo = vinculosEvento.length > 0 && (vinculosEvento.length + (autonomosEventoTotal ? 1 : 0)) > 1;
  const casaVinculoEvento = (c, vinc) => {
    if (!vinc) return true;
    const norm = normalizaImob((c.imobiliaria_nome || '').trim());
    return vinc === 'autonomo' ? !norm : `imob:${norm}` === vinc;
  };
  const matchVinculoEvento = (c) => casaVinculoEvento(c, filtroVinculoEvento);

  const buscaEventosTermo = buscaEventos.trim().toLowerCase();
  const confirmadosEventoFiltrados = confirmadosEvento.filter(c => {
    if (filtroDiaEventos) {
      const chave = c.dia ? String(c.dia).slice(0, 10) : '__sem_data__';
      if (chave !== filtroDiaEventos) return false;
    }
    if (!matchVinculoEvento(c)) return false;
    return !buscaEventosTermo || c.nome?.toLowerCase().includes(buscaEventosTermo) || (c.whatsapp || '').includes(buscaEventosTermo);
  });

  // "Marcar/desmarcar todos" afeta só o que estiver visível na busca, igual à "Agenda da instância".
  const marcarTodosEventos = () => setSelecionadosEventos(prev => new Set([...prev, ...confirmadosEventoFiltrados.map(c => c.id)]));
  const desmarcarTodosEventos = () => setSelecionadosEventos(prev => {
    const filtradosSet = new Set(confirmadosEventoFiltrados.map(c => c.id));
    return new Set([...prev].filter(id => !filtradosSet.has(id)));
  });

  // Trocar de data/vínculo já reajusta a seleção pro subconjunto que casa com AMBOS os filtros —
  // o fluxo é "escolho o dia 28 + imobiliária X, mando um disparo; abro de novo, troco o filtro,
  // mando outro texto pra esse grupo".
  const reSelecionarEvento = (dia, vinc) => {
    const alvo = confirmadosEvento.filter(c => {
      const okDia = !dia || (c.dia ? String(c.dia).slice(0, 10) : '__sem_data__') === dia;
      return okDia && casaVinculoEvento(c, vinc);
    });
    setSelecionadosEventos(new Set(alvo.map(c => c.id)));
  };
  const escolherDiaEvento = (chave) => {
    setFiltroDiaEventos(chave);
    setBuscaEventos('');
    reSelecionarEvento(chave, filtroVinculoEvento);
  };
  const escolherVinculoEvento = (chave) => {
    setFiltroVinculoEvento(chave);
    setBuscaEventos('');
    reSelecionarEvento(filtroDiaEventos, chave);
  };

  const contatosEventos = confirmadosEvento
    .filter(c => selecionadosEventos.has(c.id))
    .map((c, i) => ({ nome: c.nome, telefone: c.whatsapp, corretor_id: c.origem_tipo === 'corretor' ? c.origem_id : null, ordem: i }));

  // Números colados à mão na aba "Lista manual" — sétima origem de contatos.
  const contatosManual = origensAtivas.has('lista_manual')
    ? parseListaManual(listaManualTexto).map((c, i) => ({ nome: c.nome, telefone: c.telefone, corretor_id: null, ordem: i }))
    : [];

  // Mescla os contatos de todas as origens ativas, deduplicando por telefone normalizado
  // (mesma pessoa pode aparecer em mais de uma fonte, ex: corretor que também está no funil).
  const contatosBrutos = [
    ...(origensAtivas.has('funil') ? contatosFunil : []),
    ...(origensAtivas.has('grupo') ? contatosGrupo : []),
    ...(origensAtivas.has('corretores') ? contatosCorretores : []),
    ...(origensAtivas.has('sem_grupo') ? contatosSemGrupo : []),
    ...(origensAtivas.has('grupos_destino') ? contatosGruposDestino : []),
    ...(origensAtivas.has('agenda') ? contatosAgenda : []),
    ...(origensAtivas.has('eventos') ? contatosEventos : []),
    ...(origensAtivas.has('lista_manual') ? contatosManual : []),
  ];
  const telefonesVistos = new Set();
  const contatos = [];
  for (const c of contatosBrutos) {
    const chave = c.telefone.replace(/\D/g, '');
    if (telefonesVistos.has(chave)) continue;
    telefonesVistos.add(chave);
    contatos.push({ ...c, ordem: contatos.length });
  }

  // "grupos_destino" manda pro chat do grupo, não pra uma pessoa — os filtros de dedupe por
  // pessoa (já enviado hoje / é gestor) só fazem sentido quando alguma origem de pessoas está ativa.
  const temOrigemPessoas = origensAtivas.has('funil') || origensAtivas.has('grupo') || origensAtivas.has('corretores') || origensAtivas.has('sem_grupo') || origensAtivas.has('agenda') || origensAtivas.has('eventos') || origensAtivas.has('lista_manual');
  const origensProntas = origensAtivas.size > 0
    && (!origensAtivas.has('funil') || estagiosAtivos.size > 0)
    && (!origensAtivas.has('grupo') || !!grupoSelecionado)
    && (!origensAtivas.has('sem_grupo') || (!inbaseGruposLoading && contatosSemGrupo.length > 0))
    && (!origensAtivas.has('grupos_destino') || gruposDestinoSelecionados.size > 0)
    && (!origensAtivas.has('eventos') || !!eventoSelecionado)
    && (!origensAtivas.has('lista_manual') || contatosManual.length > 0);

  const addStep = (tipo) => setSteps(prev => [...prev, { tipo, conteudo: '', arquivo: null, preview: null, nomeOriginal: '', legenda: '' }]);
  const updateStep = (i, step) => setSteps(prev => prev.map((s, idx) => idx === i ? step : s));
  const removeStep = (i) => setSteps(prev => prev.filter((_, idx) => idx !== i));
  const moveStep = (i, dir) => {
    const arr = [...steps];
    const t = i + dir;
    if (t < 0 || t >= arr.length) return;
    [arr[i], arr[t]] = [arr[t], arr[i]];
    setSteps(arr);
  };

  const reset = () => {
    setEstagiosAtivos(new Set()); setSelecionados(new Set());
    setSteps([{ tipo: 'texto', conteudo: '', arquivo: null, preview: null, nomeOriginal: '', legenda: '' }]);
    setError('');
    setOrigensAtivas(new Set(['funil'])); setGrupoSelecionado(''); setSelecionadosGrupo(new Set());
    setSelecionadosCorretores(new Set()); setBuscaCorretores(''); setGruposDestinoSelecionados(new Set());
    setSelecionadosSemGrupo(new Set()); setBuscaSemGrupo('');
    setSelecionadosAgenda(new Set()); setBuscaAgenda('');
    setEventoSelecionado(''); setSelecionadosEventos(new Set()); setBuscaEventos(''); setFiltroDiaEventos(''); setFiltroVinculoEvento('');
    setListaManualTexto(''); setExcelManualLoading(false);
    setAgendamentoAtivo(false); setAgendarPara('');
    setProvedor('evolution'); setInstanciaMeta(''); setDelayMensagensSeg(15);
    setModelosMeta([]); setModeloMeta(''); setModeloHeaderTipo(null); setMetaHeaderFile(null); setErroModelos('');
  };
  const handleClose = () => { if (!sending) { reset(); onClose(); } };

  // Importa planilha (.xlsx/.xls/.csv, colunas "Nome"/"WhatsApp" — mesmo parser usado no resto do
  // sistema) direto pra origem "Lista manual": cada linha importada vira uma linha de texto
  // "Nome, telefone" acrescentada ao textarea, reaproveitando o parseListaManual/dedupe existentes.
  const onExcelImportManual = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setExcelManualLoading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('excel', file);
      const res = await api.post('/envios-massa/parse-excel', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const imported = res.data.data || [];
      if (!imported.length) {
        setError('Nenhum contato válido encontrado nessa planilha (esperado colunas "Nome" e "WhatsApp").');
      } else {
        const linhas = imported.map(c => c.nome ? `${c.nome}, ${c.telefone}` : c.telefone);
        setListaManualTexto(prev => (prev.trim() ? prev.trim() + '\n' : '') + linhas.join('\n'));
      }
    } catch (err) {
      setError('Erro ao ler Excel: ' + (err.response?.data?.error || err.message));
    } finally {
      setExcelManualLoading(false);
      e.target.value = '';
    }
  };

  // Exporta os contatos já selecionados/mesclados (a lista real que vai receber o disparo)
  const exportarExcel = () => {
    if (!contatos.length) return;
    const XLSX = require('xlsx');
    const rows = contatos.map((c, i) => {
      const num = String(c.telefone || '').replace(/\D/g, '');
      return {
        '#': i + 1,
        Nome: c.nome || '',
        Telefone: formatWhatsapp(num) || num,
        Numero: num,
        WhatsApp: num ? `https://wa.me/${num}` : '',
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [{ wch: 5 }, { wch: 28 }, { wch: 18 }, { wch: 15 }, { wch: 26 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Contatos');
    const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
    XLSX.writeFile(wb, `disparo-funil-corretores-${stamp}.xlsx`);
  };

  const handleSubmit = async () => {
    setError('');
    if (!origensAtivas.size) return setError('Selecione ao menos uma origem de contatos');
    if (origensAtivas.has('funil') && !estagiosAtivos.size) return setError('Selecione ao menos uma coluna do funil');
    if (origensAtivas.has('grupo') && !grupoSelecionado) return setError('Selecione o grupo do WhatsApp');
    if (origensAtivas.has('grupos_destino') && !gruposDestinoSelecionados.size) return setError('Selecione ao menos um grupo de destino');
    if (origensAtivas.has('eventos') && !eventoSelecionado) return setError('Selecione o evento');
    if (origensAtivas.has('lista_manual') && !contatosManual.length) return setError('Cole ao menos um número válido (com DDD) na lista manual de destinatários');
    if (origensAtivas.has('sem_grupo') && inbaseGruposLoading) return setError('Aguarde o cruzamento com os grupos inBase terminar');
    if (origensAtivas.has('sem_grupo') && !contatosSemGrupo.length) return setError('Nenhum contato fora dos grupos inBase selecionado');
    if (provedor === 'evolution' && !instancia) return setError('Selecione a instância WhatsApp');
    if (provedor === 'meta_oficial' && !instanciaMeta) return setError('Selecione o número da API Oficial Meta');
    if (provedor === 'meta_oficial' && !modeloMeta) return setError('Selecione um modelo aprovado da Meta');
    if (usesTemplate && modeloHeaderTipo && !metaHeaderFile) return setError(`O modelo "${modeloMeta}" precisa de ${modeloHeaderTipo === 'IMAGE' ? 'uma imagem' : 'um vídeo'} no cabeçalho — anexe o arquivo`);
    const hasContent = steps.some(s => s.tipo === 'texto' ? s.conteudo.trim() : s.arquivo);
    if (!hasContent && !usesTemplate) return setError('Adicione conteúdo à esteira (texto, imagem, vídeo, áudio ou documento)');
    if (!contatos.length) return setError('Selecione ao menos um contato');
    if (agendamentoAtivo && !agendarPara) return setError('Informe a data e hora do agendamento');
    if (agendamentoAtivo && new Date(agendarPara) <= new Date()) return setError('A data do agendamento deve ser no futuro');

    setSending(true);
    try {
      const labelsOrigem = [];
      if (origensAtivas.has('funil')) labelsOrigem.push(Array.from(estagiosAtivos).map(chave => estagios.find(e => e.chave === chave)?.label || chave).join(', '));
      if (origensAtivas.has('grupo')) labelsOrigem.push(grupos.find(g => g.id === grupoSelecionado)?.subject || 'Grupo WhatsApp');
      if (origensAtivas.has('corretores')) labelsOrigem.push('Corretores cadastrados');
      if (origensAtivas.has('sem_grupo')) labelsOrigem.push('Fora dos grupos inBase');
      if (origensAtivas.has('grupos_destino')) labelsOrigem.push(gruposDestinoSelecionados.size === 1 ? contatosGruposDestino[0]?.nome : `${gruposDestinoSelecionados.size} grupos`);
      if (origensAtivas.has('agenda')) labelsOrigem.push('Agenda da instância');
      if (origensAtivas.has('lista_manual')) labelsOrigem.push(`Lista manual (${contatosManual.length})`);
      if (origensAtivas.has('eventos')) {
        const nomeEvento = eventosDisponiveis.find(e => e.id === eventoSelecionado)?.nome || 'Evento';
        const diaLabel = diasEvento.find(d => d.chave === filtroDiaEventos)?.label;
        const vincLabel = filtroVinculoEvento === 'autonomo'
          ? 'Autônomos'
          : (vinculosEvento.find(v => v.chave === filtroVinculoEvento)?.label || null);
        const extraEvento = [diaLabel, vincLabel].filter(Boolean).join(', ');
        labelsOrigem.push(extraEvento ? `${nomeEvento} (${extraEvento})` : nomeEvento);
      }
      const origemLabel = labelsOrigem.join(' + ');
      const stepsConfig = usesTemplate ? [] : steps.map(s => s.tipo === 'texto'
        ? { tipo: 'texto', conteudo: s.conteudo || '' }
        : { tipo: s.tipo, legenda: s.legenda || '', nomeOriginal: s.nomeOriginal || '' });
      const fd = new FormData();
      fd.append('config', JSON.stringify({
        nome: `${prefixoNome} - ${origemLabel} - ${new Date().toLocaleDateString('pt-BR')}`,
        instancia_evolution: provedor === 'meta_oficial' ? instanciaMeta : instancia,
        instancia_tipo: provedor,
        delay_segundos: Number(delaySeg) || 5,
        delay_entre_mensagens_segundos: Number(delayMensagensSeg) || 2,
        contatos,
        steps: stepsConfig,
        autostart: !agendamentoAtivo,
        agendado_para: agendamentoAtivo ? agendarPara : null,
        origem,
        pular_enviados_hoje: pularEnviadosHoje,
        pular_gestores: pularGestores,
        modelo_meta: usesTemplate ? modeloMeta : undefined,
        evento_confirmacao_id: usesTemplate && eventoConfirmacaoId ? eventoConfirmacaoId : undefined,
      }));
      if (!usesTemplate) steps.forEach((s, i) => { if (s.tipo !== 'texto' && s.arquivo) fd.append(`step_${i}`, s.arquivo, s.nomeOriginal || `step_${i}`); });
      if (usesTemplate && metaHeaderFile) fd.append('meta_header_file', metaHeaderFile);
      const resp = await api.post('/envios-massa', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
      qc.invalidateQueries({ queryKey: ['campanhas-massa-funil'] });
      const ignoradosDuplicados = resp.data?.data?.ignorados_duplicados || 0;
      const ignoradosGestores = resp.data?.data?.ignorados_gestores || 0;
      const leadsNovosCriados = resp.data?.data?.leads_novos_criados || 0;
      const enfileirada = resp.data?.data?.enfileirada || false;
      if (leadsNovosCriados > 0) qc.invalidateQueries({ queryKey: ['parceiros-funil-corretores'] });
      const eraAgendamento = agendamentoAtivo;
      const horaAgendada = agendarPara;
      reset();
      setTab('historico');
      const avisos = [];
      if (eraAgendamento) avisos.push(`Campanha agendada para ${new Date(horaAgendada).toLocaleString('pt-BR')}`);
      if (enfileirada) avisos.push('Já tem outra campanha em andamento nessa instância — essa entrou na fila e começa sozinha assim que a anterior terminar');
      if (ignoradosDuplicados > 0) avisos.push(`${ignoradosDuplicados} contato(s) já tinham recebido mensagem de alguma campanha hoje`);
      if (ignoradosGestores > 0) avisos.push(`${ignoradosGestores} contato(s) eram gestores`);
      if (leadsNovosCriados > 0) avisos.push(`${leadsNovosCriados} contato(s) novo(s) entraram no funil como lead na coluna "${estagios[0]?.label || 'Novo Contato'}"`);
      if (avisos.length) alert(avisos.join('. ') + '.');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={titulo} size="xl">
      <div className="space-y-4">
        <div className="flex gap-1 border-b border-gray-100 -mt-2">
          <button
            type="button"
            onClick={() => setTab('nova')}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'nova' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            Nova campanha
          </button>
          <button
            type="button"
            onClick={() => setTab('historico')}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'historico' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            Disparos e relatório
          </button>
        </div>

        {tab === 'historico' ? (
          <CampanhasFunilHistorico />
        ) : (
      <>
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">Provedor de envio</label>
          <div className="flex gap-2">
            <button type="button" onClick={() => trocarProvedor('evolution')} className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold border transition-colors ${provedor === 'evolution' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
              Evolution API
            </button>
            <button type="button" onClick={() => trocarProvedor('meta_oficial')} className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold border transition-colors ${provedor === 'meta_oficial' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
              API Oficial Meta
            </button>
          </div>
          {provedor === 'meta_oficial' ? (
            instanciasMeta.length === 0 ? (
              <div className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">Nenhum número da API Oficial configurado. Vá em "API Oficial Meta" no menu.</div>
            ) : (
              <select value={instanciaMeta} onChange={e => setInstanciaMeta(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
                <option value="">Selecione o número...</option>
                {instanciasMeta.map(n => <option key={n.id} value={n.id}>{n.nome}{n.phone_display ? ` — ${n.phone_display}` : ''}</option>)}
              </select>
            )
          ) : (
            instancias.length === 0 ? (
              <div className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">Nenhuma instância conectada. Vá em WhatsApp para conectar.</div>
            ) : (
              <select value={instancia} onChange={e => setInstancia(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
                {instancias.map(i => <option key={i.name} value={i.name}>{i.name} — {i.profileName || i.ownerJid || ''}</option>)}
              </select>
            )
          )}

          {provedor === 'meta_oficial' && instanciaMeta && (
            <div className="border border-green-200 bg-green-50 rounded-xl p-3 space-y-2 mt-2">
              <label className="text-xs font-semibold text-green-800 block uppercase tracking-wider">Modelo de mensagem (Meta) *</label>
              {loadingModelos && <div className="flex items-center gap-2 text-sm text-green-700"><RefreshCw size={14} className="animate-spin" /> Carregando modelos aprovados...</div>}
              {erroModelos && <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2"><AlertCircle size={14} /> {erroModelos}</div>}
              {!loadingModelos && !erroModelos && modelosMeta.length === 0 && (
                <div className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">Nenhum modelo aprovado nessa conta Meta. Aprove um template no WhatsApp Manager primeiro.</div>
              )}
              {!loadingModelos && modelosMeta.length > 0 && (
                <>
                  <select value={modeloMeta} onChange={e => {
                    const name = e.target.value;
                    setModeloMeta(name);
                    setMetaHeaderFile(null);
                    setModeloHeaderTipo(name ? (modelosMeta.find(m => m.name === name)?.header_type || null) : null);
                  }} className="w-full border border-green-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-400">
                    <option value="">Selecione um modelo aprovado...</option>
                    {modelosMeta.map(m => <option key={m.id} value={m.name}>{m.name} — {m.language}{m.header_type ? ` 📎 ${m.header_type}` : ''}</option>)}
                  </select>
                  {modeloHeaderTipo && (
                    <div className="bg-white border border-blue-200 rounded-lg px-3 py-2 space-y-1.5">
                      <p className="text-xs font-semibold text-blue-800">Este modelo tem cabeçalho de {modeloHeaderTipo === 'IMAGE' ? 'imagem' : 'vídeo'} — anexe o arquivo *</p>
                      {metaHeaderFile ? (
                        <div className="flex items-center gap-2"><CheckCircle2 size={14} className="text-green-600" /><span className="text-xs text-gray-700 truncate flex-1">{metaHeaderFile.name}</span><button type="button" onClick={() => setMetaHeaderFile(null)} className="text-gray-400 hover:text-red-500"><X size={13} /></button></div>
                      ) : (
                        <button type="button" onClick={() => metaHeaderRef.current?.click()} className="flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900 underline"><Upload size={12} /> Selecionar {modeloHeaderTipo === 'IMAGE' ? 'imagem' : 'vídeo'}</button>
                      )}
                      <input ref={metaHeaderRef} type="file" className="hidden" accept={modeloHeaderTipo === 'IMAGE' ? 'image/*' : 'video/*'} onChange={e => { setMetaHeaderFile(e.target.files[0] || null); e.target.value = ''; }} />
                    </div>
                  )}
                  <p className="text-[11px] text-green-700">Pela API Oficial, quem não te respondeu nas últimas 24h só recebe mensagem via modelo aprovado. O texto enviado é o do modelo — a esteira abaixo é ignorada.</p>
                </>
              )}
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Origem dos contatos (pode combinar mais de uma)</label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => toggleOrigem('funil')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${origensAtivas.has('funil') ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
            >
              Coluna do funil
            </button>
            <button
              type="button"
              disabled={provedor === 'meta_oficial'}
              onClick={() => toggleOrigem('grupo')}
              title={provedor === 'meta_oficial' ? 'Indisponível na API Oficial Meta' : ''}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${provedor === 'meta_oficial' ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed' : origensAtivas.has('grupo') ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
            >
              Contatos de um grupo
            </button>
            <button
              type="button"
              onClick={() => toggleOrigem('corretores')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${origensAtivas.has('corretores') ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
            >
              Corretores cadastrados
            </button>
            <button
              type="button"
              disabled={provedor === 'meta_oficial'}
              onClick={() => toggleOrigem('sem_grupo')}
              title={provedor === 'meta_oficial' ? 'Indisponível na API Oficial Meta — depende da Evolution' : 'Funil + corretores cadastrados que NÃO estão em nenhum grupo com "inbase" no nome (instância fernando)'}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${provedor === 'meta_oficial' ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed' : origensAtivas.has('sem_grupo') ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
            >
              Não fazem parte de grupos
            </button>
            <button
              type="button"
              disabled={provedor === 'meta_oficial'}
              onClick={() => toggleOrigem('grupos_destino')}
              title={provedor === 'meta_oficial' ? 'Indisponível na API Oficial Meta' : ''}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${provedor === 'meta_oficial' ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed' : origensAtivas.has('grupos_destino') ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
            >
              Enviar direto pra grupo(s)
            </button>
            <button
              type="button"
              disabled={provedor === 'meta_oficial'}
              onClick={() => toggleOrigem('agenda')}
              title={provedor === 'meta_oficial' ? 'Indisponível na API Oficial Meta' : ''}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${provedor === 'meta_oficial' ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed' : origensAtivas.has('agenda') ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
            >
              Agenda da instância
            </button>
            <button
              type="button"
              onClick={() => toggleOrigem('eventos')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${origensAtivas.has('eventos') ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
            >
              Inscritos em evento
            </button>
            <button
              type="button"
              onClick={() => toggleOrigem('lista_manual')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${origensAtivas.has('lista_manual') ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
            >
              Lista manual
            </button>
          </div>
          {contatos.length > 0 && (
            <p className="text-xs text-gray-500 mt-1">{contatos.length} contato(s) únicos ao todo (duplicados entre origens são mesclados automaticamente).</p>
          )}
        </div>

        {origensAtivas.has('funil') && (
        <>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Coluna do funil (pode selecionar mais de uma)</label>
          <div className="flex flex-wrap gap-2">
            {estagios.map(col => {
              const count = (kanban[col.chave] || []).filter(c => c.whatsapp).length;
              const ativa = estagiosAtivos.has(col.chave);
              return (
                <button
                  key={col.chave}
                  type="button"
                  onClick={() => toggleEstagio(col.chave)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border ${ativa ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                >
                  {col.label} <span className={`px-1.5 rounded-full ${ativa ? 'bg-white/20' : 'bg-gray-100'}`}>{count}</span>
                </button>
              );
            })}
          </div>
        </div>

        {estagiosAtivos.size > 0 && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Contatos ({selecionados.size}/{contatosColuna.length} selecionados)
              </label>
              <div className="flex gap-2">
                <button type="button" onClick={marcarTodos} className="text-xs font-medium text-blue-600 hover:text-blue-700">Marcar todos</button>
                <button type="button" onClick={desmarcarTodos} className="text-xs font-medium text-blue-600 hover:text-blue-700">Desmarcar todos</button>
              </div>
            </div>
            {contatosColuna.length === 0 ? (
              <p className="text-xs text-gray-400">Nenhum contato com WhatsApp nessa coluna.</p>
            ) : (
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto">
                {contatosColuna.map(c => {
                  const key = `${c.tipo}:${c.id}`;
                  return (
                    <label key={key} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selecionados.has(key)}
                        onChange={() => toggleContato(key)}
                        className="shrink-0"
                      />
                      <span className="flex-1 truncate">{c.nome}</span>
                      <span className="text-xs text-gray-400 shrink-0">{formatWhatsapp(c.whatsapp)}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}
        </>
        )}

        {origensAtivas.has('grupo') && (
        <>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Grupo do WhatsApp</label>
          {!instancia ? (
            <p className="text-xs text-gray-400">Selecione uma instância acima.</p>
          ) : gruposLoading ? (
            <p className="text-xs text-gray-400">Carregando grupos...</p>
          ) : gruposError ? (
            <p className="text-xs text-red-500">{gruposError.response?.data?.error || 'Erro ao buscar grupos'}</p>
          ) : grupos.length === 0 ? (
            <p className="text-xs text-gray-400">Nenhum grupo encontrado nessa instância.</p>
          ) : (
            <select value={grupoSelecionado} onChange={e => setGrupoSelecionado(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
              <option value="">Selecione um grupo...</option>
              {grupos.map(g => <option key={g.id} value={g.id}>{g.subject} ({g.size})</option>)}
            </select>
          )}
        </div>

        {grupoSelecionado && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Contatos ({selecionadosGrupo.size}/{participantesGrupo.length} selecionados)
              </label>
              <div className="flex gap-2">
                <button type="button" onClick={marcarTodosGrupo} className="text-xs font-medium text-blue-600 hover:text-blue-700">Marcar todos</button>
                <button type="button" onClick={desmarcarTodosGrupo} className="text-xs font-medium text-blue-600 hover:text-blue-700">Desmarcar todos</button>
              </div>
            </div>
            {participantesLoading ? (
              <p className="text-xs text-gray-400">Carregando participantes...</p>
            ) : participantesError ? (
              <p className="text-xs text-red-500">{participantesError.response?.data?.error || 'Erro ao buscar participantes'}</p>
            ) : participantesGrupo.length === 0 ? (
              <p className="text-xs text-gray-400">Nenhum participante com telefone válido nesse grupo.</p>
            ) : (
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto">
                {participantesGrupo.map(p => (
                  <label key={p.telefone} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selecionadosGrupo.has(p.telefone)}
                      onChange={() => toggleContatoGrupo(p.telefone)}
                      className="shrink-0"
                    />
                    <span className="flex-1 truncate">{p.nome || 'Sem nome salvo'}</span>
                    <span className="text-xs text-gray-400 shrink-0">{formatWhatsapp(p.telefone)}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}
        </>
        )}

        {origensAtivas.has('corretores') && (
        <>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Corretores cadastrados ({selecionadosCorretores.size}/{corretoresFiltrados.length} selecionados)
            </label>
            <div className="flex gap-2">
              <button type="button" onClick={marcarTodosCorretores} className="text-xs font-medium text-blue-600 hover:text-blue-700">Marcar todos</button>
              <button type="button" onClick={desmarcarTodosCorretores} className="text-xs font-medium text-blue-600 hover:text-blue-700">Desmarcar todos</button>
            </div>
          </div>
          <input
            type="text"
            value={buscaCorretores}
            onChange={e => setBuscaCorretores(e.target.value)}
            placeholder="Buscar por nome ou imobiliária..."
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          {corretoresLoading ? (
            <p className="text-xs text-gray-400">Carregando corretores...</p>
          ) : corretoresError ? (
            <p className="text-xs text-red-500">{corretoresError.response?.data?.error || 'Erro ao buscar corretores'}</p>
          ) : corretoresFiltrados.length === 0 ? (
            <p className="text-xs text-gray-400">Nenhum corretor com WhatsApp encontrado.</p>
          ) : (
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto">
              {corretoresFiltrados.map(c => (
                <label key={c.id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selecionadosCorretores.has(c.id)}
                    onChange={() => toggleContatoCorretor(c.id)}
                    className="shrink-0"
                  />
                  <span className="flex-1 truncate">{c.nome}</span>
                  <span className="text-xs text-gray-400 truncate max-w-[140px]">{c.imobiliaria_nome || ''}</span>
                  <span className="text-xs text-gray-400 shrink-0">{formatWhatsapp(c.whatsapp)}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        </>
        )}

        {origensAtivas.has('sem_grupo') && (
        <>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Fora dos grupos inBase ({selecionadosSemGrupo.size}/{contatosSemGrupoDisponiveis.length} selecionados)
            </label>
            <div className="flex gap-2">
              <button type="button" onClick={marcarTodosSemGrupo} className="text-xs font-medium text-blue-600 hover:text-blue-700">Marcar todos</button>
              <button type="button" onClick={desmarcarTodosSemGrupo} className="text-xs font-medium text-blue-600 hover:text-blue-700">Desmarcar todos</button>
            </div>
          </div>
          <p className="text-xs text-gray-500 mb-2">
            Universo = todos os leads do funil + todos os corretores cadastrados, menos quem participa de algum grupo com "inbase" no nome (instância <b>fernando</b>, exceto "Inbase &amp; Amblard"). A exclusão vale só para esta origem — se você combinar com "Coluna do funil" ou "Corretores cadastrados", contatos que estão em grupo podem voltar por lá.
          </p>
          <input
            type="text"
            value={buscaSemGrupo}
            onChange={e => setBuscaSemGrupo(e.target.value)}
            placeholder="Buscar por nome ou telefone..."
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          {inbaseGruposLoading ? (
            <p className="text-xs text-gray-400">Cruzando com os grupos inBase… (pode levar ~1 min na primeira vez)</p>
          ) : inbaseGruposError ? (
            <p className="text-xs text-red-500">{inbaseGruposError.response?.data?.error || 'Erro ao cruzar com os grupos inBase'}</p>
          ) : contatosSemGrupoFiltrados.length === 0 ? (
            <p className="text-xs text-gray-400">Nenhum contato fora dos grupos inBase{buscaSemGrupo ? ' para essa busca' : ''}.</p>
          ) : (
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto">
              {contatosSemGrupoFiltrados.map(c => (
                <label key={c.k} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selecionadosSemGrupo.has(c.k)}
                    onChange={() => toggleContatoSemGrupo(c.k)}
                    className="shrink-0"
                  />
                  <span className="flex-1 truncate">{c.nome || '(sem nome)'}</span>
                  <span className="text-xs text-gray-400 shrink-0">{formatWhatsapp(c.telefone)}</span>
                </label>
              ))}
            </div>
          )}
          {!inbaseGruposLoading && !inbaseGruposError && (
            <p className="text-[11px] text-gray-400 mt-1">
              {contatosSemGrupoDisponiveis.length} fora de grupo · {excluidosPorGrupo} já estão em grupo inBase · {gruposInbaseVerificados} grupos verificados na instância fernando
            </p>
          )}
        </div>
        </>
        )}

        {origensAtivas.has('grupos_destino') && (
        <>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Grupos de destino ({gruposDestinoSelecionados.size}/{grupos.length} selecionados)
            </label>
            <div className="flex gap-2">
              <button type="button" onClick={marcarTodosGruposDestino} className="text-xs font-medium text-blue-600 hover:text-blue-700">Marcar todos</button>
              <button type="button" onClick={desmarcarTodosGruposDestino} className="text-xs font-medium text-blue-600 hover:text-blue-700">Desmarcar todos</button>
            </div>
          </div>
          <p className="text-xs text-gray-500 mb-2">A mensagem é enviada uma vez direto no chat de cada grupo selecionado — não extrai os contatos dele.</p>
          {!instancia ? (
            <p className="text-xs text-gray-400">Selecione uma instância acima.</p>
          ) : gruposLoading ? (
            <p className="text-xs text-gray-400">Carregando grupos...</p>
          ) : gruposError ? (
            <p className="text-xs text-red-500">{gruposError.response?.data?.error || 'Erro ao buscar grupos'}</p>
          ) : grupos.length === 0 ? (
            <p className="text-xs text-gray-400">Nenhum grupo encontrado nessa instância.</p>
          ) : (
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto">
              {grupos.map(g => (
                <label key={g.id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={gruposDestinoSelecionados.has(g.id)}
                    onChange={() => toggleGrupoDestino(g.id)}
                    className="shrink-0"
                  />
                  <span className="flex-1 truncate">{g.subject}</span>
                  <span className="text-xs text-gray-400 shrink-0">{g.size} membros</span>
                </label>
              ))}
            </div>
          )}
        </div>
        </>
        )}

        {origensAtivas.has('agenda') && (
        <>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Agenda da instância ({selecionadosAgenda.size} selecionado(s))
            </label>
            <div className="flex gap-2">
              <button type="button" onClick={marcarTodosAgenda} className="text-xs font-medium text-blue-600 hover:text-blue-700">Marcar todos</button>
              <button type="button" onClick={desmarcarTodosAgenda} className="text-xs font-medium text-blue-600 hover:text-blue-700">Desmarcar todos</button>
            </div>
          </div>
          <p className="text-xs text-gray-500 mb-2">Contatos com quem essa instância já trocou mensagem direta (sincronizado pelo WhatsApp). "Marcar/desmarcar todos" afeta só o que estiver visível na busca.</p>
          <input
            type="text"
            value={buscaAgenda}
            onChange={e => setBuscaAgenda(e.target.value)}
            placeholder="Buscar por nome ou telefone..."
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          {!instancia ? (
            <p className="text-xs text-gray-400">Selecione uma instância acima.</p>
          ) : agendaLoading ? (
            <p className="text-xs text-gray-400">Carregando agenda...</p>
          ) : agendaError ? (
            <p className="text-xs text-red-500">{agendaError.response?.data?.error || 'Erro ao buscar agenda'}</p>
          ) : contatosAgendaFiltrados.length === 0 ? (
            <p className="text-xs text-gray-400">Nenhum contato encontrado.</p>
          ) : (
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto">
              {contatosAgendaFiltrados.map(c => (
                <label key={c.telefone} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selecionadosAgenda.has(c.telefone)}
                    onChange={() => toggleContatoAgenda(c.telefone)}
                    className="shrink-0"
                  />
                  <span className="flex-1 truncate">{c.nome || 'Sem nome salvo'}</span>
                  <span className="text-xs text-gray-400 shrink-0">{formatWhatsapp(c.telefone)}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        </>
        )}

        {origensAtivas.has('eventos') && (
        <>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Evento</label>
          <select
            value={eventoSelecionado}
            onChange={e => setEventoSelecionado(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="">Selecione o evento...</option>
            {eventosDisponiveis.map(ev => (
              <option key={ev.id} value={ev.id}>{ev.nome} — {ev.total_confirmados} inscrito(s)</option>
            ))}
          </select>
        </div>

        {eventoSelecionado && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Inscritos ({selecionadosEventos.size}/{confirmadosEvento.length} selecionados)
              </label>
              <div className="flex gap-2">
                <button type="button" onClick={marcarTodosEventos} className="text-xs font-medium text-blue-600 hover:text-blue-700">Marcar todos</button>
                <button type="button" onClick={desmarcarTodosEventos} className="text-xs font-medium text-blue-600 hover:text-blue-700">Desmarcar todos</button>
              </div>
            </div>
            {diasEvento.length > 1 && (
              <div className="flex flex-wrap gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => escolherDiaEvento('')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${!filtroDiaEventos ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                >
                  Todas as datas <span className={`px-1.5 rounded-full ${!filtroDiaEventos ? 'bg-white/20' : 'bg-gray-100'}`}>{confirmadosEvento.length}</span>
                </button>
                {diasEvento.map(d => (
                  <button
                    key={d.chave}
                    type="button"
                    onClick={() => escolherDiaEvento(d.chave)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${filtroDiaEventos === d.chave ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                  >
                    {d.label} <span className={`px-1.5 rounded-full ${filtroDiaEventos === d.chave ? 'bg-white/20' : 'bg-gray-100'}`}>{d.total}</span>
                  </button>
                ))}
              </div>
            )}
            {temFiltroVinculo && (
              <div className="flex flex-wrap gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => escolherVinculoEvento('')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${!filtroVinculoEvento ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                >
                  Todos os vínculos
                </button>
                {autonomosEventoTotal > 0 && (
                  <button
                    type="button"
                    onClick={() => escolherVinculoEvento('autonomo')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${filtroVinculoEvento === 'autonomo' ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                  >
                    Autônomos / sem imobiliária <span className={`px-1.5 rounded-full ${filtroVinculoEvento === 'autonomo' ? 'bg-white/20' : 'bg-gray-100'}`}>{autonomosEventoTotal}</span>
                  </button>
                )}
                {vinculosEvento.map(v => (
                  <button
                    key={v.chave}
                    type="button"
                    onClick={() => escolherVinculoEvento(v.chave)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${filtroVinculoEvento === v.chave ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                  >
                    {v.label} <span className={`px-1.5 rounded-full ${filtroVinculoEvento === v.chave ? 'bg-white/20' : 'bg-gray-100'}`}>{v.total}</span>
                  </button>
                ))}
              </div>
            )}
            <input
              type="text"
              value={buscaEventos}
              onChange={e => setBuscaEventos(e.target.value)}
              placeholder="Buscar por nome ou telefone (ex: um corretor específico)..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            {eventoDetalheLoading ? (
              <p className="text-xs text-gray-400">Carregando inscritos...</p>
            ) : eventoDetalheError ? (
              <p className="text-xs text-red-500">{eventoDetalheError.response?.data?.error || 'Erro ao buscar inscritos do evento'}</p>
            ) : confirmadosEvento.length === 0 ? (
              <p className="text-xs text-gray-400">Nenhum inscrito com WhatsApp cadastrado nesse evento ainda.</p>
            ) : confirmadosEventoFiltrados.length === 0 ? (
              <p className="text-xs text-gray-400">Nenhum inscrito encontrado para essa busca.</p>
            ) : (
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto">
                {confirmadosEventoFiltrados.map(c => {
                  const dataFmt = c.dia ? new Date(`${String(c.dia).slice(0, 10)}T00:00:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '';
                  const detalhe = [dataFmt, c.horario ? `às ${c.horario.slice(0, 5)}` : null].filter(Boolean).join(' ');
                  return (
                    <label key={c.id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selecionadosEventos.has(c.id)}
                        onChange={() => toggleContatoEvento(c.id)}
                        className="shrink-0"
                      />
                      <span className="flex-1 truncate">{c.nome || 'Sem nome'}</span>
                      <span className="text-xs text-gray-400 shrink-0 truncate max-w-[110px]">{(c.imobiliaria_nome || '').trim() || 'Autônomo'}</span>
                      {detalhe && <span className="text-xs text-gray-400 shrink-0">{detalhe}</span>}
                      <span className="text-xs text-gray-400 shrink-0">{formatWhatsapp(c.whatsapp)}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}
        </>
        )}

        {origensAtivas.has('lista_manual') && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">Lista manual de destinatários</label>
              <button
                type="button"
                onClick={() => excelManualInputRef.current?.click()}
                disabled={excelManualLoading}
                className="flex items-center gap-1.5 px-2.5 py-1 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 disabled:opacity-50"
              >
                <FileSpreadsheet size={13} />{excelManualLoading ? 'Importando...' : 'Importar planilha (Excel)'}
              </button>
              <input ref={excelManualInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onExcelImportManual} />
            </div>
            <textarea
              value={listaManualTexto}
              onChange={e => setListaManualTexto(e.target.value)}
              rows={5}
              placeholder={'Um por linha. Ex:\n41999998888\nJoão Silva, +55 41 98888-7777'}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            <p className="text-xs text-gray-400 mt-1">
              {contatosManual.length > 0
                ? `${contatosManual.length} número(s) válido(s) reconhecido(s). Opcional: "Nome, número" para dar nome ao contato.`
                : 'Cole os números com DDD (um por linha, opcional "Nome, número") ou importe uma planilha .xlsx/.xls/.csv com colunas "Nome" e "WhatsApp".'}
            </p>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {usesTemplate ? 'Mensagem' : 'Esteira de mensagens (enviadas em sequência)'}
            </label>
            {!usesTemplate && <span className="text-xs text-gray-400">{steps.length} bloco(s)</span>}
          </div>
          {usesTemplate ? (
            <div className="space-y-2">
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Modelo aprovado da Meta selecionado (<strong>{modeloMeta}</strong>) — a mensagem enviada é o texto do próprio modelo.
                Não é preciso montar esteira nem escrever mensagem personalizada; a Meta não entrega mensagem livre fora da janela de 24h.
              </p>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Amarrar a um evento (opcional)</label>
                <select value={eventoConfirmacaoId} onChange={e => setEventoConfirmacaoId(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
                  <option value="">Não amarrar</option>
                  {eventosAtivosTemplate.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
                </select>
                <p className="text-xs text-gray-400 mt-1">Se o template tiver o botão "Confirmar presença", amarrar faz o clique cair neste evento (manda QR + endereço + localização de volta).</p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 mb-2">
                <button type="button" onClick={() => addStep('texto')} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100"><Type size={13} /> + Texto</button>
                <button type="button" onClick={() => addStep('imagem')} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-xs font-medium hover:bg-green-100"><Image size={13} /> + Imagem</button>
                <button type="button" onClick={() => addStep('video')} className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg text-xs font-medium hover:bg-purple-100"><Video size={13} /> + Vídeo</button>
                <button type="button" onClick={() => addStep('audio')} className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 text-orange-700 rounded-lg text-xs font-medium hover:bg-orange-100"><Music size={13} /> + Áudio</button>
                <button type="button" onClick={() => addStep('documento')} className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200"><FileText size={13} /> + Documento</button>
              </div>
              <div className="space-y-2">
                {steps.map((step, i) => (
                  <EsteiraStepCard
                    key={i}
                    step={step}
                    index={i}
                    total={steps.length}
                    onUpdate={s => updateStep(i, s)}
                    onRemove={() => removeStep(i)}
                    onMoveUp={() => moveStep(i, -1)}
                    onMoveDown={() => moveStep(i, 1)}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="flex flex-wrap gap-6">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Intervalo entre mensagens do mesmo contato (segundos)</label>
            <input type="number" min={1} max={600} value={delayMensagensSeg} onChange={e => setDelayMensagensSeg(Number(e.target.value))} className="w-28 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
            <p className="text-xs text-gray-400 mt-1">Ex: mandou a 1ª msg pro lead, espera esse tempo e manda a 2ª.</p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Intervalo entre contatos (segundos)</label>
            <input type="number" min={2} max={3600} value={delaySeg} onChange={e => setDelaySeg(Number(e.target.value))} className="w-28 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
            <p className="text-xs text-gray-400 mt-1">Ex: 180 = espera 3 min antes de começar a mandar pro próximo contato.</p>
          </div>
        </div>

        {temOrigemPessoas && (
        <div className="space-y-1.5">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={pularEnviadosHoje}
              onChange={e => setPularEnviadosHoje(e.target.checked)}
              className="shrink-0"
            />
            Não enviar para números que já receberam mensagem de outra campanha hoje
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={pularGestores}
              onChange={e => setPularGestores(e.target.checked)}
              className="shrink-0"
            />
            Não enviar para números cadastrados como gestor de imobiliária
          </label>
        </div>
        )}

        <div className="space-y-1.5 border-t border-gray-100 pt-3">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={agendamentoAtivo}
              onChange={e => setAgendamentoAtivo(e.target.checked)}
              className="shrink-0"
            />
            Agendar disparo pra depois (em vez de disparar agora)
          </label>
          {agendamentoAtivo && (
            <input
              type="datetime-local"
              value={agendarPara}
              onChange={e => setAgendarPara(e.target.value)}
              min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          )}
        </div>

        {origensProntas && (
          <p className="text-xs text-gray-500">
            <strong>{contatos.length}</strong> contato(s) selecionado(s) vão receber {usesTemplate ? <>a mensagem do modelo <strong>{modeloMeta}</strong></> : `${steps.length} bloco(s) da esteira, em sequência`}{agendamentoAtivo && agendarPara ? ` — agendado para ${new Date(agendarPara).toLocaleString('pt-BR')}` : ''}.
          </p>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex items-center justify-between gap-2 pt-3 border-t border-gray-100">
          <button
            type="button"
            onClick={exportarExcel}
            disabled={!contatos.length}
            title="Baixa em .xlsx os números que vão receber o disparo"
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40"
          >
            <Download size={15} /> Exportar Excel ({contatos.length})
          </button>
          <div className="flex gap-2">
          <button type="button" onClick={handleClose} disabled={sending} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
          <button type="button" onClick={handleSubmit} disabled={sending} className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-secondary disabled:opacity-50">
            {agendamentoAtivo ? <CalendarClock size={15} /> : <Send size={15} />}
            {sending ? (agendamentoAtivo ? 'Agendando...' : 'Disparando...') : (agendamentoAtivo ? 'Agendar disparo' : 'Disparar mensagens')}
          </button>
          </div>
        </div>
      </>
        )}
      </div>
    </Modal>
  );
}

function EditarContatoModal({ isOpen, onClose, card }) {
  const qc = useQueryClient();
  const isCorretor = card?.tipo !== 'lead';
  const [nome, setNome] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [email, setEmail] = useState('');
  const [tipo, setTipo] = useState('autonomo');
  const [imobiliariaId, setImobiliariaId] = useState('');
  const [cpf, setCpf] = useState('');
  const [creci, setCreci] = useState('');
  const [cargo, setCargo] = useState('corretor');
  const [observacoes, setObservacoes] = useState('');

  const { data: imobiliarias } = useQuery({
    queryKey: ['imobiliarias'],
    queryFn: () => api.get('/imobiliarias').then(r => r.data.data),
    enabled: isOpen,
  });

  useEffect(() => {
    if (isOpen && card) {
      setNome(card.nome || '');
      setWhatsapp(formatWhatsapp(card.whatsapp || ''));
      setEmail(card.email || '');
      setTipo(card.corretor_tipo || 'autonomo');
      setImobiliariaId(card.imobiliaria_id || '');
      setCpf(card.cpf || '');
      setCreci(card.creci || '');
      setCargo(card.cargo || 'corretor');
      setObservacoes(card.observacoes || '');
    }
  }, [isOpen, card]);

  const salvarMut = useMutation({
    mutationFn: () => {
      const base = {
        nome, whatsapp, email: email.trim() || null,
        tipo, imobiliaria_id: tipo === 'imobiliaria' ? (imobiliariaId || null) : null,
      };
      return card.tipo === 'lead'
        ? api.patch(`/parceiros/leads/${card.id}/contato`, base)
        : api.put(`/corretores/${card.id}`, { ...base, cpf, creci, cargo, observacoes });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['parceiros-funil-corretores'] });
      onClose();
    },
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Editar ${isCorretor ? 'corretor' : 'lead'} — ${card?.nome || ''}`} size="sm">
      <form onSubmit={e => { e.preventDefault(); salvarMut.mutate(); }} className="space-y-3">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Nome</label>
          <input value={nome} onChange={e => setNome(e.target.value)} className="input-field w-full" required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">WhatsApp</label>
          <input
            value={whatsapp}
            onChange={e => setWhatsapp(formatWhatsapp(e.target.value))}
            className="input-field w-full"
            placeholder="Ex: 44 99948-9948"
            inputMode="numeric"
            required
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Email</label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="input-field w-full"
            placeholder="opcional"
          />
        </div>
        {isCorretor && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">CPF</label>
              <input value={cpf} onChange={e => setCpf(e.target.value)} className="input-field w-full" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">CRECI</label>
              <input value={creci} onChange={e => setCreci(e.target.value)} className="input-field w-full" />
            </div>
          </div>
        )}
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Tipo</label>
          <select value={tipo} onChange={e => setTipo(e.target.value)} className="input-field w-full">
            <option value="autonomo">Autônomo</option>
            <option value="imobiliaria">Vinculado a Imobiliária</option>
          </select>
        </div>
        {tipo === 'imobiliaria' && (
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Imobiliária</label>
            <select value={imobiliariaId} onChange={e => setImobiliariaId(e.target.value)} className="input-field w-full">
              <option value="">Selecione...</option>
              {(imobiliarias || []).map(i => <option key={i.id} value={i.id}>{i.nome}</option>)}
            </select>
          </div>
        )}
        {isCorretor && (
          <>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Cargo</label>
              <select value={cargo} onChange={e => setCargo(e.target.value)} className="input-field w-full">
                <option value="corretor">Corretor</option>
                <option value="gestor">Gestor</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Observações</label>
              <textarea value={observacoes} onChange={e => setObservacoes(e.target.value)} rows={2} className="input-field w-full resize-y" />
            </div>
          </>
        )}
        {salvarMut.isError && (
          <p className="text-sm text-red-600">{salvarMut.error?.response?.data?.error || 'Erro ao salvar'}</p>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
          <button type="submit" disabled={salvarMut.isPending} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-secondary disabled:opacity-50">
            {salvarMut.isPending ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function FunilCard({ card: c, colIdx, estagios, proximoEvento, advance, innerRef, draggableProps, dragHandleProps, isDragging }) {
  const qc = useQueryClient();
  const [notaOpen, setNotaOpen] = useState(false);
  const [agendaOpen, setAgendaOpen] = useState(false);
  const [visitaOpen, setVisitaOpen] = useState(false);
  const [contatoOpen, setContatoOpen] = useState(false);
  const [reagendarOpen, setReagendarOpen] = useState(false);
  const [eventoOpen, setEventoOpen] = useState(false);

  return (
    <div
      ref={innerRef}
      {...draggableProps}
      {...dragHandleProps}
      className={`bg-white rounded-lg p-3 shadow-sm ${isDragging ? 'rotate-1 shadow-lg' : ''}`}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold text-sm text-gray-800">{c.nome}</p>
        <div className="flex items-center gap-1 shrink-0">
          {c.tipo === 'lead' && (
            <span className="flex items-center gap-1 text-[10px] font-medium text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded-full">
              <UserPlus size={10} /> Lead
            </span>
          )}
          <button
            onClick={() => setContatoOpen(true)}
            title="Editar contato"
            className="p-1 text-gray-400 hover:text-primary hover:bg-gray-50 rounded"
          >
            <Pencil size={12} />
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-500">{c.imobiliaria_nome || (c.tipo === 'lead' ? 'Ainda não cadastrado' : '—')}</p>
      {c.origem && (
        <p className="text-xs text-gray-400 mt-0.5">{ORIGEM_LABELS[c.origem] || c.origem}</p>
      )}
      {proximoEvento && (
        c.evento_presenca ? (
          <div className={`flex items-start gap-1 mt-1.5 text-[11px] font-medium px-2 py-1 rounded-lg ${
            c.evento_presenca.status === 'compareceu' ? 'bg-green-100 text-green-800' : 'bg-emerald-50 text-emerald-700'
          }`}>
            <Check size={12} className="mt-0.5 shrink-0" />
            <span>
              {c.evento_presenca.status === 'compareceu' ? 'Compareceu' : 'Presença confirmada'} — {proximoEvento.nome}
              {c.evento_presenca.dia && (
                <span className="text-emerald-600/70"> · {format(new Date(`${String(c.evento_presenca.dia).slice(0, 10)}T00:00:00`), 'dd/MM', { locale: ptBR })}{c.evento_presenca.horario ? ` ${c.evento_presenca.horario}` : ''}</span>
              )}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-1 mt-1.5 text-[11px] text-gray-400 px-2 py-1 rounded-lg bg-gray-50">
            <X size={12} className="shrink-0" />
            <span>Sem confirmação — {proximoEvento.nome}</span>
          </div>
        )
      )}
      {c.tipo !== 'lead' && (
        <div className="flex flex-wrap gap-1 mt-2">
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            c.temperatura === 'ativo' ? 'bg-green-100 text-green-700' :
            c.temperatura === 'morno' ? 'bg-yellow-100 text-yellow-700' :
            c.temperatura === 'parado' ? 'bg-red-100 text-red-700' :
            'bg-blue-100 text-blue-700'
          }`}>
            {c.temperatura || 'novo'}
          </span>
        </div>
      )}
      <p className="text-xs text-gray-400 mt-2">
        {c.last_interaction_at
          ? `Última interação: ${formatDistanceToNow(new Date(c.last_interaction_at), { locale: ptBR, addSuffix: true })}`
          : 'Sem interação registrada'}
      </p>
      {c.data_proxima_acao && (
        <p className="text-xs text-primary mt-0.5 font-medium">
          Próximo contato: {format(new Date(c.data_proxima_acao), 'dd/MM/yyyy', { locale: ptBR })}
        </p>
      )}
      {c.visita_cal_uid && (
        <div className="flex items-center gap-2 mt-0.5">
          <a
            href={`https://agenda.minhainbase.com.br/booking/${c.visita_cal_uid}`}
            target="_blank" rel="noreferrer"
            className="flex items-center gap-1 text-xs text-purple-600 hover:underline w-fit"
          >
            <ExternalLink size={10} /> Ver visita no cal.diy
          </a>
          {c.visita_cal_booking_id && (
            <button
              onClick={() => setReagendarOpen(true)}
              className="text-xs text-blue-600 hover:underline"
            >
              Reagendar
            </button>
          )}
        </div>
      )}
      {c.visita_start_time && (
        <p className="text-xs text-gray-400 mt-0.5">
          {format(new Date(c.visita_start_time), "dd/MM 'às' HH:mm", { locale: ptBR })}
        </p>
      )}

      {c.email && (
        <p className="text-xs text-gray-400 mt-1 truncate" title={c.email}>{c.email}</p>
      )}

      {c.whatsapp && (
        <a
          href={`https://wa.me/55${c.whatsapp.replace(/\D/g, '')}`}
          target="_blank" rel="noreferrer"
          className="flex items-center justify-center gap-1 w-full mt-2 py-1 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100"
        >
          <MessageCircle size={12} /> WhatsApp
        </a>
      )}

      <div className="flex gap-1 mt-1">
        <button
          onClick={() => setNotaOpen(true)}
          className="flex-1 flex items-center justify-center gap-1 py-1 text-xs bg-gray-50 hover:bg-gray-100 text-gray-600 rounded border border-gray-200"
        >
          <StickyNote size={12} /> Anotações
        </button>
        <button
          onClick={() => setAgendaOpen(true)}
          className="flex-1 flex items-center justify-center gap-1 py-1 text-xs bg-gray-50 hover:bg-gray-100 text-gray-600 rounded border border-gray-200"
        >
          <CalendarClock size={12} /> Agenda
        </button>
        <button
          onClick={() => setVisitaOpen(true)}
          className="flex-1 flex items-center justify-center gap-1 py-1 text-xs bg-purple-50 hover:bg-purple-100 text-purple-700 rounded border border-purple-200"
        >
          <CalendarPlus size={12} /> Agendar Visita
        </button>
      </div>

      {c.whatsapp && (
        <button
          onClick={() => setEventoOpen(true)}
          className="flex items-center justify-center gap-1 w-full mt-1 py-1 text-xs bg-amber-50 hover:bg-amber-100 text-amber-700 rounded border border-amber-200"
        >
          <PartyPopper size={12} /> Confirmar presença em evento
        </button>
      )}

      {estagios[colIdx]?.chave === 'contato_feito'
        && estagios.some(e => e.chave === 'convite_enviado') && estagios.some(e => e.chave === 'nao_respondeu') ? (
        <div className="flex gap-1 mt-1">
          <button
            onClick={() => advance.mutate({ id: c.id, tipo: c.tipo, novoEstagio: 'convite_enviado' })}
            disabled={advance.isPending}
            className="flex-1 py-1 text-xs bg-gray-50 hover:bg-gray-100 text-gray-600 rounded border border-gray-200 disabled:opacity-50"
          >
            Respondeu →
          </button>
          <button
            onClick={() => advance.mutate({ id: c.id, tipo: c.tipo, novoEstagio: 'nao_respondeu' })}
            disabled={advance.isPending}
            className="flex-1 py-1 text-xs bg-red-50 hover:bg-red-100 text-red-600 rounded border border-red-200 disabled:opacity-50"
          >
            Não respondeu
          </button>
        </div>
      ) : (
        <button
          onClick={() => advance.mutate({ id: c.id, tipo: c.tipo, novoEstagio: estagios[colIdx + 1]?.chave })}
          disabled={colIdx >= estagios.length - 1 || advance.isPending}
          className="w-full mt-1 py-1 text-xs bg-gray-50 hover:bg-gray-100 text-gray-600 rounded border border-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {colIdx < estagios.length - 1 ? 'Avançar →' : '✓ Final'}
        </button>
      )}

      <AnotacaoModal isOpen={notaOpen} onClose={() => setNotaOpen(false)} card={c} />
      <AgendaModal isOpen={agendaOpen} onClose={() => setAgendaOpen(false)} card={c} />
      <AgendarVisitaModal isOpen={visitaOpen} onClose={() => setVisitaOpen(false)} card={c} />
      <EditarContatoModal isOpen={contatoOpen} onClose={() => setContatoOpen(false)} card={c} />
      <ConfirmarPresencaEventoModal
        isOpen={eventoOpen}
        onClose={() => setEventoOpen(false)}
        contatoInicial={{ tipo: c.tipo === 'lead' ? 'lead' : 'corretor', id: c.id, nome: c.nome, whatsapp: c.whatsapp }}
      />
      <ReagendarVisitaModal
        isOpen={reagendarOpen}
        onClose={() => setReagendarOpen(false)}
        onSuccess={() => {
          setReagendarOpen(false);
          qc.invalidateQueries({ queryKey: ['parceiros-funil-corretores'] });
        }}
        bookingId={c.visita_cal_booking_id}
        nome={c.nome}
        startTime={c.visita_start_time}
        empreendimento={c.visita_empreendimento}
      />
    </div>
  );
}

// Exclusão de etapa — se houver leads/corretores nela, o backend recusa (409 ETAPA_COM_LEADS)
// até informar destino_chave; aqui a gente já calcula a contagem local (kanban) pra mostrar o
// seletor de destino direto, sem esperar o 409 (que ainda serve de rede de segurança se a
// contagem local estiver desatualizada).
function ExcluirEstagioModal({ isOpen, onClose, estagio, estagios, kanban, onDeleted }) {
  const [destino, setDestino] = useState('');
  const [erro, setErro] = useState('');
  const [totalPendente, setTotalPendente] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) { setDestino(''); setErro(''); setTotalPendente(null); }
  }, [isOpen, estagio]);

  if (!estagio) return null;

  const contagemLocal = (kanban[estagio.chave] || []).length;
  const precisaDestino = totalPendente !== null ? totalPendente > 0 : contagemLocal > 0;
  const outras = estagios.filter(e => e.chave !== estagio.chave);

  const confirmar = async () => {
    setErro(''); setLoading(true);
    try {
      await api.delete(`/parceiros/funil-estagios/${estagio.chave}`, { data: destino ? { destino_chave: destino } : {} });
      onDeleted();
    } catch (err) {
      if (err.response?.status === 409) {
        setTotalPendente(err.response.data.total);
      } else {
        setErro(err.response?.data?.error || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Excluir etapa "${estagio.label}"`} size="sm">
      <div className="space-y-3">
        {precisaDestino ? (
          <>
            <p className="text-sm text-gray-600">
              Essa etapa tem <strong>{totalPendente ?? contagemLocal}</strong> contato(s). Escolha pra qual etapa eles devem ser movidos:
            </p>
            <select value={destino} onChange={e => setDestino(e.target.value)} className="input-field w-full">
              <option value="">Selecione...</option>
              {outras.map(e => <option key={e.chave} value={e.chave}>{e.label}</option>)}
            </select>
          </>
        ) : (
          <p className="text-sm text-gray-600">Essa etapa está vazia. Tem certeza que quer excluí-la?</p>
        )}
        {erro && <p className="text-sm text-red-600">{erro}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
          <button
            type="button"
            onClick={confirmar}
            disabled={loading || (precisaDestino && !destino)}
            className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
          >
            {loading ? 'Excluindo...' : 'Excluir'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default function FunilCorretoresKanban() {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [disparoOpen, setDisparoOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [editingChave, setEditingChave] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [excluindoEstagio, setExcluindoEstagio] = useState(null);
  const [criandoColuna, setCriandoColuna] = useState(false);
  const [novoLabel, setNovoLabel] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['parceiros-funil-corretores'],
    queryFn: () => api.get('/parceiros/funil-corretores').then(r => r.data.data),
    refetchInterval: 30000,
  });

  const invalidar = () => qc.invalidateQueries({ queryKey: ['parceiros-funil-corretores'] });

  const advance = useMutation({
    mutationFn: ({ id, tipo, novoEstagio }) => {
      const url = tipo === 'lead' ? `/parceiros/leads/${id}/estagio` : `/parceiros/${id}/estagio`;
      return api.patch(url, { estagio_funil: novoEstagio });
    },
    onSuccess: invalidar,
  });

  const criarEstagioMut = useMutation({
    mutationFn: (label) => api.post('/parceiros/funil-estagios', { label }),
    onSuccess: () => { invalidar(); setCriandoColuna(false); setNovoLabel(''); },
  });

  const renomearEstagioMut = useMutation({
    mutationFn: ({ chave, label }) => api.put(`/parceiros/funil-estagios/${chave}`, { label }),
    onSuccess: () => { invalidar(); setEditingChave(null); },
  });

  const reordenarEstagiosMut = useMutation({
    mutationFn: (ordem) => api.put('/parceiros/funil-estagios/reordenar', { ordem }),
    onSuccess: invalidar,
  });

  if (isLoading) return <div className="p-8 text-center text-gray-400">Carregando...</div>;

  const kanban = data?.grupos || {};
  const estagios = data?.estagios || [];

  const term = search.trim().toLowerCase();
  const matches = (c) => !term
    || c.nome?.toLowerCase().includes(term)
    || c.whatsapp?.includes(term)
    || c.email?.toLowerCase().includes(term)
    || c.imobiliaria_nome?.toLowerCase().includes(term);

  function onDragEnd(result) {
    if (!result.destination) return;
    const { draggableId, source, destination, type } = result;

    if (type === 'COLUNA') {
      if (destination.index === source.index) return;
      const novaOrdem = estagios.map(e => e.chave);
      const [movida] = novaOrdem.splice(source.index, 1);
      novaOrdem.splice(destination.index, 0, movida);
      reordenarEstagiosMut.mutate(novaOrdem);
      return;
    }

    if (destination.droppableId === source.droppableId) return;
    const [tipo, id] = draggableId.split(':');
    advance.mutate({ id, tipo, novoEstagio: destination.droppableId });
  }

  const iniciarEdicao = (col) => { setEditingChave(col.chave); setEditLabel(col.label); };
  const salvarEdicao = () => {
    if (!editLabel.trim() || editLabel.trim() === estagios.find(e => e.chave === editingChave)?.label) {
      setEditingChave(null);
      return;
    }
    renomearEstagioMut.mutate({ chave: editingChave, label: editLabel.trim() });
  };
  const criarColuna = () => {
    if (!novoLabel.trim()) { setCriandoColuna(false); return; }
    criarEstagioMut.mutate(novoLabel.trim());
  };

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-gray-800">Funil de Leads Corretores</h1>

      <div className="flex justify-between items-center gap-2">
        <div className="relative w-72">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nome, whatsapp, email ou imobiliária..."
            className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setDisparoOpen(true)}
            className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
          >
            <Send size={15} /> Mensagens automáticas
          </button>
          <button
            onClick={() => setModalOpen(true)}
            className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-secondary"
          >
            <Plus size={15} /> Novo Lead
          </button>
        </div>
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="board-colunas" type="COLUNA" direction="horizontal">
          {(providedBoard) => (
            <div ref={providedBoard.innerRef} {...providedBoard.droppableProps} className="flex gap-4 overflow-x-auto pb-4 items-start">
              {estagios.map((col, colIdx) => {
                const cards = (kanban[col.chave] || []).filter(matches);
                return (
                  <Draggable key={col.chave} draggableId={col.chave} index={colIdx}>
                    {(provColuna) => (
                      <div ref={provColuna.innerRef} {...provColuna.draggableProps} className="flex-shrink-0 w-72">
                        <div className={`${col.cor} text-white px-2.5 py-2 rounded-t-lg flex items-center gap-1.5`}>
                          <span {...provColuna.dragHandleProps} className="cursor-grab text-white/70 hover:text-white shrink-0">
                            <GripVertical size={14} />
                          </span>
                          {editingChave === col.chave ? (
                            <input
                              autoFocus
                              value={editLabel}
                              onChange={e => setEditLabel(e.target.value)}
                              onBlur={salvarEdicao}
                              onKeyDown={e => { if (e.key === 'Enter') salvarEdicao(); if (e.key === 'Escape') setEditingChave(null); }}
                              className="flex-1 min-w-0 bg-white/20 text-white text-sm font-semibold rounded px-1.5 py-0.5 outline-none placeholder-white/60"
                            />
                          ) : (
                            <span className="flex-1 min-w-0 truncate text-sm font-semibold">{col.label}</span>
                          )}
                          <span className="bg-white bg-opacity-30 text-xs px-2 py-0.5 rounded-full shrink-0">
                            {term ? `${cards.length}/${(kanban[col.chave] || []).length}` : cards.length}
                          </span>
                          {editingChave === col.chave ? (
                            <button onClick={salvarEdicao} title="Salvar" className="text-white/70 hover:text-white shrink-0"><Check size={13} /></button>
                          ) : (
                            <>
                              <button onClick={() => iniciarEdicao(col)} title="Renomear etapa" className="text-white/70 hover:text-white shrink-0"><Pencil size={12} /></button>
                              <button onClick={() => setExcluindoEstagio(col)} title="Excluir etapa" className="text-white/70 hover:text-white shrink-0"><Trash2 size={12} /></button>
                            </>
                          )}
                        </div>
                        <Droppable droppableId={col.chave} type="CARD">
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              className={`bg-gray-100 rounded-b-lg p-2 space-y-2 min-h-32 ${snapshot.isDraggingOver ? 'bg-blue-50' : ''}`}
                            >
                              {cards.map((c, index) => (
                                <Draggable key={`${c.tipo}:${c.id}`} draggableId={`${c.tipo}:${c.id}`} index={index}>
                                  {(prov, snap) => (
                                    <FunilCard
                                      card={c}
                                      colIdx={colIdx}
                                      estagios={estagios}
                                      proximoEvento={data?.proximo_evento}
                                      advance={advance}
                                      innerRef={prov.innerRef}
                                      draggableProps={prov.draggableProps}
                                      dragHandleProps={prov.dragHandleProps}
                                      isDragging={snap.isDragging}
                                    />
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {providedBoard.placeholder}

              <div className="flex-shrink-0 w-72">
                {criandoColuna ? (
                  <div className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-2 space-y-2">
                    <input
                      autoFocus
                      value={novoLabel}
                      onChange={e => setNovoLabel(e.target.value)}
                      placeholder="Nome da nova etapa"
                      className="input-field w-full text-sm"
                      onKeyDown={e => { if (e.key === 'Enter') criarColuna(); if (e.key === 'Escape') { setCriandoColuna(false); setNovoLabel(''); } }}
                    />
                    <div className="flex gap-1.5">
                      <button onClick={criarColuna} disabled={criarEstagioMut.isPending} className="flex-1 py-1 text-xs bg-primary text-white rounded hover:bg-secondary disabled:opacity-50">
                        {criarEstagioMut.isPending ? 'Criando...' : 'Criar'}
                      </button>
                      <button onClick={() => { setCriandoColuna(false); setNovoLabel(''); }} className="flex-1 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200">Cancelar</button>
                    </div>
                    {criarEstagioMut.isError && (
                      <p className="text-xs text-red-600">{criarEstagioMut.error?.response?.data?.error || 'Erro ao criar etapa'}</p>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => setCriandoColuna(true)}
                    className="w-full h-11 flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-400 hover:text-primary hover:border-primary transition-colors"
                  >
                    <Plus size={16} /> Nova etapa
                  </button>
                )}
              </div>
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <NovoLeadModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
      <DisparoMassaModal isOpen={disparoOpen} onClose={() => setDisparoOpen(false)} kanban={kanban} estagios={estagios} />
      <ExcluirEstagioModal
        isOpen={!!excluindoEstagio}
        onClose={() => setExcluindoEstagio(null)}
        estagio={excluindoEstagio}
        estagios={estagios}
        kanban={kanban}
        onDeleted={() => { setExcluindoEstagio(null); invalidar(); }}
      />
    </div>
  );
}
